import { useEffect, useMemo, useState, useCallback } from "react";
import { http, fmtEUR, warnZeroStock, stockBadge } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { toastFiscalOk, toastFiscalFail } from "@/lib/fiscalToast";
import { confirmDialog } from "@/lib/confirmDialog";
import { mergeServerTables } from "@/hooks/useTablesStream";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import MobilePaymentDialog from "@/components/MobilePaymentDialog";
import QuickNoteDialog from "@/components/QuickNoteDialog";
import SendMessageDialog from "@/components/SendMessageDialog";
import ServingScreen from "@/components/ServingScreen";
import {
  Utensils,
  ClipboardList,
  LogOut,
  ChevronLeft,
  Send,
  Receipt,
  Plus,
  Minus,
  Clock,
  Trash2,
  Pencil,
  ArrowLeftRight,
  History,
  ListChecks,
  Wrench,
  HandCoins,
  Move,
  Printer,
  ArrowDownAZ,
  ArrowUpAZ,
  UsersRound,
  MessageSquare,
  ConciergeBell,
} from "lucide-react";

/**
 * MobileRestaurant — touch-first layout for waitstaff using a phone.
 *
 *  3 screens driven by a `view` state machine:
 *    • tables   — section tabs (+ «Όλα») + 3-col grid of all configured tables
 *    • order    — drilldown: cart on top, category pills, product grid,
 *                 sticky action bar (Αποστολή / Λογαριασμός)
 *    • myorders — every table with an active session (open_items OR
 *                 submitted_batches). Tap row → opens the same order view.
 *
 *  Polls /tables every 5s for cross-device live updates so that another
 *  device's mutation reflects within a few seconds. Cart edits are
 *  persisted via PUT /tables/{id}/open-items on every mutation so a
 *  network blip cannot lose lines.
 *
 *  Field schema mirrors what the backend actually returns from
 *  GET /tables (open_items / submitted_batches / session_opened_at).
 */

const ALL_SECTIONS = "__all__";

// --- Helpers ---------------------------------------------------
// Live total for a table = sum of open_items + sum of submitted_batches.
function tableTotal(t) {
  if (!t) return 0;
  let s = 0;
  for (const it of t.open_items || []) {
    const gross = Number(it.qty || 0) * (Number(it.price || 0) + Number(it.modifier_total || 0));
    s += it.discount_percent ? gross * (1 - Number(it.discount_percent) / 100) : gross;
  }
  for (const b of t.submitted_batches || []) {
    for (const it of b.items || []) {
      if (it.corrected_deleted) continue;
      if (it.paid_partial) continue;
      const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
      const gross = Number(it.qty || 0) * unit;
      s += it.discount_percent ? gross * (1 - Number(it.discount_percent) / 100) : gross;
    }
  }
  return s;
}
function tableHasSession(t) {
  if (!t) return false;
  if ((t.open_items || []).length > 0) return true;
  for (const b of t.submitted_batches || []) {
    if ((b.items || []).some((it) => !it.corrected_deleted && !it.paid_partial)) return true;
  }
  return false;
}

export default function MobileRestaurant() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("tables");
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(ALL_SECTIONS);
  // Occupancy filter applied INSIDE the active section: null = όλα,
  // "free" = ελεύθερα, "busy" = σε χρήση. Resets when section changes.
  const [occupancyFilter, setOccupancyFilter] = useState(null);
  const [tables, setTables] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentGroups, setCommentGroups] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [openTableId, setOpenTableId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  // Comment dialog state — only for one cart line at a time.
  const [cmtDlg, setCmtDlg] = useState({ open: false, idx: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
  // Feb 2026: bottom-sheet με ενεργούς χειριστές + SendMessageDialog.
  // Ανοίγει με tap στο όνομα του χειριστή στη γραμμή top-right.
  const [operatorsOpen, setOperatorsOpen] = useState(false);
  const [onlineOperators, setOnlineOperators] = useState([]);
  const [msgTarget, setMsgTarget] = useState(null);
  // Confirm dialog for discarding unsubmitted items when navigating back.
  const [discardDlg, setDiscardDlg] = useState(false);
  // Table-transfer dialog state.
  const [transferDlg, setTransferDlg] = useState(false);
  // Εργασίες — 4-action menu mirroring desktop order-tasks-popover.
  const [tasksDlgOpen, setTasksDlgOpen] = useState(false);
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  // Διαγραφή Τραπεζιού — confirmation.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // Ιούν 2026 — force-delete τραπεζιού σταλμένου στην ταμειακή: κωδικός διαχειριστή.
  const [deleteForceCode, setDeleteForceCode] = useState("");
  const [deleteForceOpen, setDeleteForceOpen] = useState(false);
  // Submitted-item action menu (delete / re-order via correction).
  const [batchActionDlg, setBatchActionDlg] = useState(null); // { batchNo, itemIdx, item } | null
  // Pending corrections on submitted-batch items — mirrors desktop's
  // `corrections` state. Format: { "batchNo:idx": { deleted: true } }
  // Stays local until the operator presses Καταχώρηση, which flushes
  // via POST /apply-corrections (same backend endpoint as desktop).
  // For sent-to-register items the backend stamps corrected_deleted +
  // fiscal_line_no so the next /ftext/restaurant-order can emit VOID_LINE.
  const [corrections, setCorrections] = useState({});
  // Collapsible previous-orders panel — closed by default; tap to reveal.
  const [prevOpen, setPrevOpen] = useState(false);
  // 3-option «Καταχώρηση Παραγγελίας» dialog (mirrors desktop Restaurant.jsx).
  const [submitChoiceOpen, setSubmitChoiceOpen] = useState(false);
  // Highlights the most recently-used table for 5 seconds so the
  // operator can quickly spot the last table they interacted with
  // (helpful when they realise they made a mistake).
  const [recentlyUsedTableId, setRecentlyUsedTableId] = useState(null);
  const markRecentlyUsed = (tid) => {
    if (!tid) return;
    setRecentlyUsedTableId(tid);
    setTimeout(() => {
      setRecentlyUsedTableId((cur) => (cur === tid ? null : cur));
    }, 5000);
  };
  // Partial-send picker — Map<`${batch_no}:${item_idx}`, qty>.
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialQty, setPartialQty] = useState(new Map());
  // «Απόδειξη» (fiscal close) state — mirrors desktop Restaurant.jsx.
  const [closeDlgOpen, setCloseDlgOpen] = useState(false);
  const [flushWarnOpen, setFlushWarnOpen] = useState(false);
  const [closePayments, setClosePayments] = useState([]);
  const [closeComment, setCloseComment] = useState("");
  const [closeNonFiscal, setCloseNonFiscal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [settings, setSettings] = useState({});
  const [printOverride, setPrintOverride] = useState("");
  // Μερική Είσπραξη — separate from Μερική Αποστολή (which only pushes to
  // the ταμειακή). Pays for selected items, leaves the rest open.
  // Three-stage machine («pick» → «warn» → «pay») mirroring desktop.
  const [paymentPartialOpen, setPaymentPartialOpen] = useState(false);
  const [paymentPartialQty, setPaymentPartialQty] = useState(new Map());
  const [paymentPartialStage, setPaymentPartialStage] = useState("pick"); // pick | warn | pay
  const [paymentPartialSplit, setPaymentPartialSplit] = useState([]);
  const [paymentPartialCashOnly, setPaymentPartialCashOnly] = useState(false);

  // Live table reference (always derived from `tables`).
  const openTable = useMemo(
    () => tables.find((t) => t.id === openTableId) || null,
    [tables, openTableId],
  );
  const cart = useMemo(() => openTable?.open_items || [], [openTable]);

  // ---------- Initial data ----------
  useEffect(() => {
    (async () => {
      try {
        const [p, c, s, cg, cm, py, pr, st] = await Promise.all([
          http.get("/products?channel=restaurant"),
          http.get("/categories"),
          http.get("/restaurant-sections"),
          http.get("/comment-groups"),
          http.get("/comments"),
          http.get("/payments").catch(() => ({ data: [] })),
          http.get("/printers").catch(() => ({ data: [] })),
          http.get("/settings").catch(() => ({ data: {} })),
        ]);
        setProducts(p.data || []);
        const cats = (c.data || []).filter((x) => !x.hidden);
        setCategories(cats);
        setActiveCat(cats[0]?.id || null);
        setSections(s.data || []);
        setCommentGroups(cg.data || []);
        setComments(cm.data || []);
        setPayments(py.data || []);
        setPrinters(pr.data || []);
        setSettings(st.data || {});
      } catch {
        toast.error("Αποτυχία φόρτωσης μενού");
      }
    })();
  }, []);

  // ---------- Tables: initial fetch + SSE live updates ----------
  const reloadTables = useCallback(async () => {
    try {
      const r = await http.get("/tables");
      // merge-safe: μη σβήνεις το τοπικό αστάλτο cart (βλ. useTablesStream)
      setTables((prev) => mergeServerTables(prev, r.data || []));
    } catch { /* offline tolerant */ }
  }, []);
  useEffect(() => { reloadTables(); }, [reloadTables]);
  useEffect(() => {
    // Per-minute tick for «time at table» readout.
    const tick = setInterval(() => setNow(Date.now()), 30000);
    // Slow safety-net poll (every 30s, merge-safe) in case SSE has dropped
    // and the browser's auto-reconnect hasn't kicked in yet.
    const safety = setInterval(reloadTables, 30000);
    return () => { clearInterval(tick); clearInterval(safety); };
  }, [reloadTables]);

  // Online operators polling — only while the sheet is open, plus one
  // initial fetch when the user taps the name (so it's fresh).
  useEffect(() => {
    if (!operatorsOpen) return;
    let cancelled = false;
    const fetchNow = async () => {
      try {
        const r = await http.get("/users/online");
        if (!cancelled) setOnlineOperators(r.data?.operators || []);
      } catch { /* silent */ }
    };
    fetchNow();
    const t = setInterval(fetchNow, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [operatorsOpen]);

  // ---------- SSE — live push from backend on every table mutation ----------
  // Mounted exactly once per session. EventSource doesn't support custom
  // headers, so the JWT travels via the ?token query string (validated
  // server-side just like the Authorization header).
  useEffect(() => {
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("auth_token")) || "";
    if (!token) return;
    const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${baseUrl}/api/tables/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
    } catch {
      return;
    }
    let opens = 0;
    es.onopen = () => {
      opens += 1;
      if (opens > 1) reloadTables(); // reconnect → refetch όσα χάθηκαν στο κενό
    };
    es.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (!m || !m.type) return;
        if (m.deleted && m.table_id) {
          setTables((prev) => prev.filter((t) => t.id !== m.table_id));
          return;
        }
        if (m.type === "tables.refreshed") {
          // bulk change (set-count, import, …) → re-pull from scratch
          reloadTables();
          return;
        }
        if (m.table) {
          setTables((prev) => {
            const i = prev.findIndex((t) => t.id === m.table.id);
            if (i < 0) return [...prev, m.table];
            const next = [...prev];
            // Feb 2026 — cart is LOCAL-only until commit. If SSE
            // would wipe our uncommitted cart with an empty server
            // snapshot, KEEP the local open_items. Mirrors the desktop
            // useTablesStream guard.
            const localOpen = prev[i].open_items || [];
            const incomingOpen = m.table.open_items || [];
            // Ιούν 2026: αν αυξήθηκαν τα submitted_batches (μόλις έγινε
            // submit), το cart καταχωρήθηκε — μην το κρατάς (διπλό σύνολο).
            const keepLocal =
              localOpen.length > 0 &&
              incomingOpen.length === 0 &&
              (m.table.submitted_batches || []).length <=
                (prev[i].submitted_batches || []).length;
            next[i] = {
              ...m.table,
              open_items: keepLocal ? localOpen : incomingOpen,
            };
            return next;
          });
        }
      } catch { /* invalid frame, ignore */ }
    };
    es.onerror = () => {
      // EventSource will auto-reconnect; nothing to do here. The safety
      // 60-s poll keeps the UI consistent during outages.
    };
    return () => { try { es.close(); } catch { /* noop */ } };
  }, [reloadTables]);

  // ---------- View transitions ----------
  const openTableOrder = async (tbl) => {
    setBusy(true);
    try {
      // Pull fresh so we don't open a stale cart.
      await reloadTables();
      setOpenTableId(tbl.id);
      setPrevOpen(false); // collapse history when navigating to a new table
      setCorrections({}); // discard any leftover pending corrections from a previous table
      setView("order");
    } catch {
      toast.error("Αποτυχία ανοίγματος τραπεζιού");
    } finally { setBusy(false); }
  };

  // ---------- Cart mutations (LOCAL-ONLY until commit) ----------
  //
  // Feb 2026 — the cart lives in local React state only. Adding /
  // editing / removing lines no longer hits PUT /open-items. The table
  // only becomes "occupied" on other terminals when the operator
  // explicitly submits via Αποστολή στην ταμειακή / Αποθήκευση /
  // Μερική Αποστολή — those flows POST the cart items in the body of
  // /submit-batch. This matches the desktop Restaurant behavior and
  // the explicit user request («η καταχώρηση να γίνεται μόνο αν
  // πατήσεις αποστολή στην ταμειακή ή μερική αποστολή»).
  const persistOpenItems = (next) => {
    if (!openTableId) return;
    setTables((prev) => prev.map((t) => (t.id === openTableId ? { ...t, open_items: next } : t)));
  };

  // Ιούν 2026: κοινό wrapper για submit-batch — ανανεώνει και τα stock badges.
  const submitBatchApi = async () => {
    const r = await http.post(`/tables/${openTableId}/submit-batch`, { items: cart });
    // Το cart καταχωρήθηκε ως batch — καθάρισε το τοπικό αντίγραφο ώστε το
    // επόμενο SSE snapshot να μην αθροίσει cart+batch (διπλό σύνολο).
    setTables((prev) =>
      prev.map((t) =>
        t.id === openTableId
          ? { ...(r.data && r.data.id ? r.data : t), open_items: [] }
          : t,
      ),
    );
    http.get("/products?channel=restaurant")
      .then((res) => setProducts(res.data || []))
      .catch(() => {});
    return r;
  };

  const addProduct = (p) => {
    warnZeroStock(p);
    const lines = [...cart];
    const allowCmt = !!p.allow_comment;
    // Items that allow comments are kept as distinct rows (qty=1) so each
    // can carry its own modifier selection. Plain items dedupe by product.
    let newIdx;
    if (!allowCmt) {
      const i = lines.findIndex(
        (x) => x.product_id === p.id && !(x.comments && x.comments.length) && !x.free_text,
      );
      if (i >= 0) {
        lines[i] = { ...lines[i], qty: Number(lines[i].qty || 1) + 1 };
        newIdx = i;
      } else {
        lines.push({
          product_id: p.id,
          description: p.description,
          qty: 1,
          price: Number(p.price || 0),
          vat_rate: p.vat_rate,
          department: p.department,
          allow_comment: false,
          comment_group_ids: [],
          comments: [],
          comment: "",
          free_text: null,
          modifier_total: 0,
        });
        newIdx = lines.length - 1;
      }
    } else {
      lines.push({
        product_id: p.id,
        description: p.description,
        qty: 1,
        price: Number(p.price || 0),
        vat_rate: p.vat_rate,
        department: p.department,
        allow_comment: true,
        comment_group_ids: p.comment_group_ids || [],
        comments: [],
        comment: "",
        free_text: null,
        modifier_total: 0,
      });
      newIdx = lines.length - 1;
    }
    persistOpenItems(lines);
    if (allowCmt && p.auto_open_comment) {
      openCommentFor(newIdx, lines[newIdx]);
    }
  };

  const incLine = (idx, delta) => {
    const n = [...cart];
    const q = Number(n[idx].qty || 1) + delta;
    if (q <= 0) { n.splice(idx, 1); persistOpenItems(n); return; }
    n[idx] = { ...n[idx], qty: q };
    persistOpenItems(n);
  };
  const removeLine = (idx) => {
    const n = [...cart]; n.splice(idx, 1); persistOpenItems(n);
  };

  // ---------- Comments dialog ----------
  const openCommentFor = (idx, line) => {
    const it = line || cart[idx];
    if (!it) return;
    const sel = {};
    const pref = {};
    // Preserve the original qty: a saved comment with quantity=-1 means
    // «ΧΩΡΙΣ», qty>=1 means N× selection. Default to 1 when unset (legacy).
    (it.comments || []).forEach((c) => {
      const q = Number(c.quantity);
      sel[c.id] = q === -1 ? -1 : (q && q > 0 ? q : 1);
      if (c.prefix) pref[c.id] = c.prefix;
    });
    setCmtDlg({ open: true, idx, selections: sel, prefixes: pref, activePrefix: null, freeText: it.free_text || "" });
  };

  const cmtLine = useMemo(
    () => (cmtDlg.idx == null ? null : cart[cmtDlg.idx]),
    [cmtDlg.idx, cart],
  );
  const cmtGroupsForLine = useMemo(() => {
    if (!cmtLine) return [];
    const ids = cmtLine.comment_group_ids || [];
    return commentGroups
      .filter((g) => ids.includes(g.id) && g.is_active !== false)
      .sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id))
      .map((g) => {
        const items = comments
          .filter((c) => c.group_id === g.id && c.is_active !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        // Bucket by radio_group so each subgroup ξεχωρίζει visually.
        // Standalone comments (no radio_group) collapse into a `__plain__`
        // bucket displayed without a subheader.
        const bucketMap = new Map();
        const buckets = [];
        items.forEach((c) => {
          const key = c.radio_group || "__plain__";
          if (!bucketMap.has(key)) {
            const b = {
              key,
              label: key === "__plain__" ? null : key,
              items: [],
              multiple: !!c.radio_multiple,
            };
            bucketMap.set(key, b);
            buckets.push(b);
          }
          // If any sibling has radio_multiple, the whole bucket is multi.
          if (c.radio_multiple) bucketMap.get(key).multiple = true;
          bucketMap.get(key).items.push(c);
        });
        return { ...g, items, buckets };
      });
  }, [cmtLine, commentGroups, comments]);

  const toggleCommentChip = (c) => {
    setCmtDlg((d) => {
      const next = { ...d.selections };
      const pref = { ...d.prefixes };
      // Already selected? → toggle off entirely.
      if (next[c.id]) {
        delete next[c.id];
        delete pref[c.id];
        return { ...d, selections: next, prefixes: pref };
      }
      // Radio behavior: when this comment belongs to a radio_group and
      // the subgroup is single-select (radio_multiple !== true), clear
      // every NON-splittable sibling in the same group_id + radio_group.
      // Splittable siblings keep their qty so «2× ζάχαρη» can coexist
      // with a freshly picked «μέτριο» under the same «ψήσιμο» bucket.
      if (c.radio_group && !c.radio_multiple) {
        comments
          .filter(
            (x) =>
              x.group_id === c.group_id &&
              x.radio_group === c.radio_group &&
              !x.splittable,
          )
          .forEach((x) => { delete next[x.id]; });
      }
      next[c.id] = 1;
      if (d.activePrefix) pref[c.id] = d.activePrefix;
      return { ...d, selections: next, prefixes: pref, activePrefix: null };
    });
  };

  // Πρόθεμα (ΜΕ/ΧΩΡΙΣ/ΛΙΓΟ/ΠΟΛΥ/ΜΟΝΟ/EXTRA): οπλίζεται και εφαρμόζεται
  // στο επόμενο σχόλιο που θα επιλεγεί — ίδια λογική με το desktop.
  const MOBILE_PREFIXES = ["ΜΕ", "ΧΩΡΙΣ", "ΛΙΓΟ", "ΠΟΛΥ", "ΜΟΝΟ", "EXTRA"];
  const togglePrefix = (p) =>
    setCmtDlg((d) => ({ ...d, activePrefix: d.activePrefix === p ? null : p }));

  // Splittable tiles: qty cycles -1 (ΧΩΡΙΣ) ← 0 (absent) → 1 → 2 → 3…
  // The − button stops at -1; the + button has no upper bound.
  const incSplit = (c) => {
    setCmtDlg((d) => {
      const next = { ...d.selections };
      const pref = { ...d.prefixes };
      const cur = next[c.id] || 0;
      if (cur === -1) delete next[c.id]; // από «ΧΩΡΙΣ» το + πάει στο 0 (χωρίς χρέωση)
      else {
        next[c.id] = cur + 1;
        if (cur === 0 && d.activePrefix) pref[c.id] = d.activePrefix;
      }
      return { ...d, selections: next, prefixes: pref, activePrefix: cur === 0 ? null : d.activePrefix };
    });
  };
  const decSplit = (c) => {
    setCmtDlg((d) => {
      const next = { ...d.selections };
      const cur = next[c.id] || 0;
      if (cur === 0) next[c.id] = -1;
      else if (cur === 1) delete next[c.id];
      else if (cur === -1) { /* stop at ΧΩΡΙΣ */ }
      else next[c.id] = cur - 1;
      return { ...d, selections: next };
    });
  };

  const saveComment = () => {
    if (cmtDlg.idx == null) return;
    const out = [];
    let total = 0;
    Object.entries(cmtDlg.selections).forEach(([cid, rawQty]) => {
      const c = comments.find((x) => x.id === cid);
      const qty = Number(rawQty || 0);
      if (!c || qty === 0) return;
      // Label rules matching the desktop:
      //   qty === -1  → «ΧΩΡΙΣ Name»     (treated as deduction regardless of flag)
      //   qty === 1   → «[Πρόθεμα] Name» (ή «ΧΩΡΙΣ Name» αν is_deduction)
      //   qty > 1     → «N× [Πρόθεμα] Name»
      const pref = cmtDlg.prefixes?.[cid] || null;
      const base = pref ? `${pref} ${c.name}` : c.name;
      let label;
      if (qty === -1) label = `ΧΩΡΙΣ ${c.name}`;
      else if (qty > 1) label = `${qty}× ${base}`;
      else label = c.is_deduction && !pref ? `ΧΩΡΙΣ ${c.name}` : base;
      out.push({
        id: c.id,
        name: c.name,
        label,
        prefix: pref,
        price_delta: c.price_delta,
        is_deduction: !!c.is_deduction,
        color: c.color,
        radio_group: c.radio_group,
        splittable: c.splittable,
        quantity: qty,
      });
      // Price contribution:
      //   qty > 0:  sign × price_delta × qty  (sign flips on is_deduction)
      //   qty = -1: always subtract price_delta (forced deduction)
      const sign = c.is_deduction ? -1 : 1;
      if (qty > 0) total += sign * Number(c.price_delta || 0) * qty;
      else if (qty === -1 && c.is_deduction) total -= Number(c.price_delta || 0);
    });
    const ft = (cmtDlg.freeText || "").trim();
    const labels = out.map((x) => x.label);
    if (ft) labels.push(ft);
    const next = [...cart];
    next[cmtDlg.idx] = {
      ...next[cmtDlg.idx],
      comments: out,
      comment: labels.join(", "),
      free_text: ft || null,
      modifier_total: total,
    };
    persistOpenItems(next);
    setCmtDlg({ open: false, idx: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
  };

  // ---------- Helpers (parity with desktop Restaurant.jsx) ----------
  /** Every line across all submitted batches that has not yet been sent
   *  to the cash register (drops deleted/paid_partial/sent_to_register). */
  const allUnsentSelections = (tableData) => {
    const out = [];
    for (const b of tableData?.submitted_batches || []) {
      (b.items || []).forEach((it, i) => {
        if (it.corrected_deleted) return;
        if (it.sent_to_register) return;
        if (it.paid_partial) return;
        out.push({ batch_no: b.batch_no, item_idx: i, qty: Number(it.qty || 0) });
      });
    }
    return out;
  };

  /** Push to ftext/restaurant-order + mark-items-sent. Selections may carry
   *  an explicit qty for partial sends; omitted ⇒ full line. */
  const sendToRegister = async (selections, freshTable) => {
    const baseTable = freshTable || openTable;
    if (!baseTable) return;
    const items = [];
    for (const { batch_no, item_idx, qty } of selections) {
      const b = (baseTable.submitted_batches || []).find((x) => x.batch_no === batch_no);
      const it = b?.items?.[item_idx];
      if (!it || it.corrected_deleted || it.paid_partial) continue;
      const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
      const effQty = qty != null && qty < Number(it.qty) ? Number(qty) : Number(it.qty);
      items.push({
        description: it.description,
        qty: effQty,
        price: Math.max(0, unit),
        vat_rate: Number(it.vat_rate || 24),
        department: it.department,
        comments: it.comments || [],
        free_text: it.free_text || null,
        modifier_total: Number(it.modifier_total || 0),
      });
    }
    // VOID_LINEs για ήδη σταλμένα-στην-ταμειακή είδη που διαγράφηκαν —
    // ίδια λογική με desktop: στέλνονται ΜΑΖΙ σε κάθε αποστολή.
    const voidLines = [];
    for (const b of baseTable.submitted_batches || []) {
      for (const it of b.items || []) {
        if (
          it.corrected_deleted &&
          it.sent_to_register &&
          typeof it.fiscal_line_no === "number" &&
          !it.voided_on_register
        ) {
          voidLines.push(it.fiscal_line_no);
        }
      }
    }
    if (items.length === 0 && voidLines.length === 0) {
      toast.error("Δεν υπάρχουν είδη προς αποστολή");
      return null;
    }
    const r = await http.post("/ftext/restaurant-order", {
      table_nr: baseTable.device_number,
      table_name: baseTable.name,
      items,
      void_lines: voidLines,
    });
    if (!r.data?.ok) {
      toastFiscalFail();
      return null;
    }
    let updated = null;
    if (selections.length > 0 && items.length > 0) {
      const t = await http.post(`/tables/${baseTable.id}/mark-items-sent`, { selections });
      updated = t.data;
    }
    if (voidLines.length > 0) {
      const t = await http.post(`/tables/${baseTable.id}/mark-voided`, { fiscal_line_nos: voidLines });
      updated = t.data;
    }
    toastFiscalOk();
    return updated;
  };

  /** Απομένουν χρεωμένα είδη στο τραπέζι; (αγνοεί διαγραμμένα/πληρωμένα) */
  const hasChargedItems = (tableData) => {
    if ((tableData?.open_items || []).length > 0) return true;
    for (const b of tableData?.submitted_batches || []) {
      for (const it of b.items || []) {
        if (!it.corrected_deleted && !it.paid_partial) return true;
      }
    }
    return false;
  };

  /** Μετά από αποστολή διαγραφών: αν δεν έμεινε χρεωμένο είδος, ρώτα αν
   *  θα ελευθερωθεί το τραπέζι — Ναι: εντολή κλεισίματος στην ταμειακή
   *  + εκκαθάριση, Όχι: μόνο η διαγραφή (ήδη στάλθηκε). */
  const maybeOfferFreeTable = async (tableData) => {
    if (!tableData || hasChargedItems(tableData)) return;
    const yes = await confirmDialog({
      message: `Το ${tableData.name} δεν έχει πλέον χρεωμένα είδη. Να ελευθερωθεί το τραπέζι;`,
      confirmLabel: "ΝΑΙ",
      cancelLabel: "ΟΧΙ",
    });
    if (!yes) return;
    try {
      const r = await http.post("/ftext/restaurant-close", {
        table_nr: tableData.device_number,
        table_name: tableData.name,
        payments: [],
      });
      if (!r.data?.ok) {
        toastFiscalFail();
        return;
      }
      await http.post(`/tables/${tableData.id}/clear-session`);
      toast.success(`Το ${tableData.name} ελευθερώθηκε`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ελευθέρωσης τραπεζιού");
    }
  };

  // ---------- «Καταχώρηση» menu — desktop parity ----------
  const openSubmitChoice = () => {
    const hasCart = cart.length > 0;
    const hasUnsent = openTable ? allUnsentSelections(openTable).length > 0 : false;
    const hasCorr = correctionsCount > 0;
    if (!hasCart && !hasUnsent && !hasCorr) {
      toast.error("Δεν υπάρχει τίποτα προς καταχώρηση ή αποστολή");
      return;
    }
    setSubmitChoiceOpen(true);
  };

  /** Απλή Καταχώρηση: flush pending corrections + persist new items
   *  as a batch (kitchen tickets only). Mirrors desktop handleSimpleSubmit. */
  const handleSimpleSubmit = async () => {
    if (!openTableId) return;
    setBusy(true);
    try {
      const corrFresh = await _flushCorrections();
      const base = corrFresh || openTable;
      if (cart.length > 0) {
        await submitBatchApi();
      }
      // If any pending fiscal voids exist after corrections (sent items
      // marked deleted), ship them to the register so the cash machine
      // gets the VOID_LINE — same defensive logic as desktop.
      const pendingVoids = (base?.submitted_batches || []).some((b) =>
        (b.items || []).some(
          (it) => it.corrected_deleted && it.sent_to_register
            && typeof it.fiscal_line_no === "number" && !it.voided_on_register,
        ),
      );
      if (pendingVoids) {
        await sendToRegister([], base);
      }
      toast.success(`Καταχωρήθηκε στο ${openTable?.name || ""}`.trim());
      setSubmitChoiceOpen(false);
      // After Καταχώρηση return to tables menu (μέρος του spec που ζητήθηκε).
      markRecentlyUsed(openTableId);
      setOpenTableId(null);
      setView("tables");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  /** Αποστολή στην Ταμειακή: flush corrections + persist + flush ALL
   *  unsent to fiscal printer. */
  const handleFullSendToRegister = async () => {
    if (!openTableId) return;
    setBusy(true);
    try {
      const corrFresh = await _flushCorrections();
      let baseTable = corrFresh || openTable;
      if (cart.length > 0) {
        const r = await submitBatchApi();
        baseTable = r?.data || baseTable;
      }
      // Pull fresh so we have any other-device additions / corrections.
      const fresh = await http.get(`/tables`);
      const refreshed = (fresh.data || []).find((t) => t.id === openTableId) || baseTable;
      const selections = allUnsentSelections(refreshed);
      const hasVoids = (refreshed.submitted_batches || []).some((b) =>
        (b.items || []).some(
          (it) => it.corrected_deleted && it.sent_to_register
            && typeof it.fiscal_line_no === "number" && !it.voided_on_register,
        ),
      );
      if (selections.length === 0 && !hasVoids) {
        toast.error("Δεν υπάρχουν είδη προς αποστολή");
        setSubmitChoiceOpen(false);
        return;
      }
      const updated = await sendToRegister(selections, refreshed);
      // Αν διαγράφηκε και το τελευταίο είδος → πρόταση ελευθέρωσης τραπεζιού.
      await maybeOfferFreeTable(updated || refreshed);
      setSubmitChoiceOpen(false);
      markRecentlyUsed(openTableId);
      setOpenTableId(null);
      setView("tables");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποστολής στην ταμειακή");
    } finally { setBusy(false); }
  };

  /** Μερική Αποστολή — flush corrections + open picker. */
  const handlePartialStart = async () => {
    if (!openTableId) return;
    setBusy(true);
    try {
      const corrFresh = await _flushCorrections();
      let baseTable = corrFresh || openTable;
      // Persist any current cart so it shows up in the picker.
      if (cart.length > 0) {
        const r = await submitBatchApi();
        baseTable = r?.data || baseTable;
        // Feb 2026 — the cart has been moved into submitted_batches by
        // the request above; clear the LOCAL open_items copy so
        // handlePartialConfirm's "empty selection" fallback doesn't
        // POST the same items again and end up with two batches.
        persistOpenItems([]);
      }
      const sel = allUnsentSelections(baseTable);
      if (sel.length === 0) {
        // Καμία γραμμή προς αποστολή — αν όμως εκκρεμούν διαγραφές ήδη
        // σταλμένων ειδών, στείλε ΜΟΝΟ τα VOID_LINE στην ταμειακή.
        const hasVoids = (baseTable?.submitted_batches || []).some((b) =>
          (b.items || []).some(
            (it) => it.corrected_deleted && it.sent_to_register
              && typeof it.fiscal_line_no === "number" && !it.voided_on_register,
          ),
        );
        if (hasVoids) {
          const updated = await sendToRegister([], baseTable);
          await maybeOfferFreeTable(updated || baseTable);
          setSubmitChoiceOpen(false);
          markRecentlyUsed(openTableId);
          setOpenTableId(null);
          setView("tables");
          return;
        }
        toast.error("Δεν υπάρχουν είδη προς αποστολή");
        return;
      }
      setPartialQty(new Map());
      setSubmitChoiceOpen(false);
      setPartialOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  /** Confirm partial send with the chosen qty per line. */
  const handlePartialConfirm = async () => {
    if (!openTableId) return;
    const fresh = await http.get(`/tables`);
    const refreshed = (fresh.data || []).find((t) => t.id === openTableId) || openTable;
    const selections = [];
    for (const [key, qty] of partialQty.entries()) {
      if (!qty || qty <= 0) continue;
      const [bn, idx] = key.split(":").map(Number);
      selections.push({ batch_no: bn, item_idx: idx, qty });
    }
    if (selections.length === 0) {
      // Iter88 mobile parity: κενή επιλογή → «Απλή Καταχώρηση».
      // Persist ό,τι έχει το cart χωρίς αποστολή στην ταμειακή, ίδια
      // ροή με desktop.
      setBusy(true);
      try {
        if (cart.length > 0) {
          await submitBatchApi();
        }
        setPartialOpen(false);
        setPartialQty(new Map());
        markRecentlyUsed(openTableId);
        setOpenTableId(null);
        setView("tables");
        toast.success("Αποθηκεύτηκε χωρίς αποστολή στην ταμειακή");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα");
      } finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      const updated = await sendToRegister(selections, refreshed);
      await maybeOfferFreeTable(updated || refreshed);
      setPartialOpen(false);
      setPartialQty(new Map());
      markRecentlyUsed(openTableId);
      setOpenTableId(null);
      setView("tables");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποστολής");
    } finally { setBusy(false); }
  };
  // (Mobile no longer fires a browser-print fallback for proforma. The
  // backend's `raw_hex` ESC/POS payload printed by the POS-PC v3 poller
  // is the single source of truth — having a second browser-print path
  // produced duplicate slips on Kiosk-Mode tablets.)

  const printBill = async () => {
    if (!openTableId) return;
    setBusy(true);
    try {
      const r = await http.post(`/tables/${openTableId}/print-proforma`);
      // Backend enqueues a single ESC/POS `raw_hex` job that the POS PC's
      // Companion v3 poller picks up. No browser-print fallback here —
      // the queue path produces the high-fidelity slip with business
      // header, items, total and CODE128 barcode all in one cut.
      const printer = r.data?.printer_name || "";
      const total = Number(r.data?.total || 0);
      toast.success(
        `🖨️ Λογαριασμός εκτυπώθηκε${printer ? ` στο ${printer}` : ""}${total ? ` · ${total.toFixed(2)} €` : ""}`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία εκτύπωσης λογαριασμού");
    } finally { setBusy(false); }
  };

  // ---------- «Απόδειξη» — fiscal close (desktop parity) ----------
  // 1. Tap «Απόδειξη» → if unsent items exist → flushWarnOpen
  //                  → else open payment dialog directly.
  // 2. flushWarn «Συνέχεια» → ftext/restaurant-order all unsent +
  //                  mark-items-sent → open payment dialog.
  // 3. Payment dialog → POST /ftext/restaurant-close with selected
  //                  payment methods → clear-session → return to grid.
  const totalsTotal = useMemo(() => tableTotal(openTable), [openTable]);
  const onApodeixiClick = () => {
    if (!openTable) return;
    const unsent = allUnsentSelections(openTable);
    if (cart.length > 0 || unsent.length > 0) {
      setFlushWarnOpen(true);
    } else {
      // All items already on the register — jump straight to payment.
      setCloseDlgOpen(true);
    }
  };

  const handleFlushAndClose = async () => {
    if (!openTableId) return;
    setBusy(true);
    try {
      let baseTable = openTable;
      // 1) Persist any open_items so they appear in the unsent list.
      if (cart.length > 0) {
        const r = await submitBatchApi();
        baseTable = r?.data || baseTable;
      }
      // 2) Pull fresh and ship every still-unsent item to the register.
      const fresh = await http.get(`/tables`);
      const refreshed = (fresh.data || []).find((t) => t.id === openTableId) || baseTable;
      const sel = allUnsentSelections(refreshed);
      if (sel.length > 0) {
        await sendToRegister(sel, refreshed);
      }
      // 3) Open the payment dialog so the operator picks Cash / Card.
      setFlushWarnOpen(false);
      setCloseDlgOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποστολής στην ταμειακή");
    } finally { setBusy(false); }
  };

  // «Συνέχιση Είσπραξης» — skip flushing; pre-load the partial-pay flow
  // with EVERY paid-able item selected (including the unsent ones) and
  // mark `cashOnly=true` so the unsent portion is settled WITHOUT a
  // fiscal pass. The pay-stage banner explains the breakdown to the
  // operator so the customer can be charged accordingly.
  const continueCollectionCashOnly = async () => {
    if (!openTableId || !openTable) return;
    setBusy(true);
    try {
      let baseTable = openTable;
      // First persist the cart so its items are visible as submitted_batches
      // (they remain non-fiscal until /partial-payment send_unsent_to_register).
      if (cart.length > 0) {
        const r = await submitBatchApi();
        baseTable = r?.data || baseTable;
      }
      // Build a full-table selection (qty = full available qty per row).
      const qtyMap = new Map();
      for (const b of baseTable.submitted_batches || []) {
        (b.items || []).forEach((it, i) => {
          if (it.corrected_deleted || it.paid_partial) return;
          qtyMap.set(`${b.batch_no}:${i}`, Number(it.qty || 0));
        });
      }
      if (qtyMap.size === 0) {
        toast.error("Δεν υπάρχουν είδη προς είσπραξη");
        return;
      }
      setPaymentPartialQty(qtyMap);
      setPaymentPartialSplit([]);
      setPaymentPartialCashOnly(true); // critical: skip ECR send for unsent items
      setPaymentPartialStage("pay");
      setFlushWarnOpen(false);
      setPaymentPartialOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  // Helper for the pay-stage banner — splits the picked items into
  // «can use card» (already fiscal) vs «cash only» (still unsent) sums.
  const paymentBreakdown = useMemo(() => {
    if (!openTable) return null;
    let fiscalSum = 0;
    let cashOnlySum = 0;
    let fiscalCount = 0;
    let cashOnlyCount = 0;
    for (const [key, qty] of paymentPartialQty.entries()) {
      const [bn, ii] = key.split(":").map(Number);
      const b = (openTable.submitted_batches || []).find((x) => x.batch_no === bn);
      const it = b?.items?.[ii];
      if (!it) continue;
      const line = Number(qty || 0) * (Number(it.price || 0) + Number(it.modifier_total || 0));
      if (it.sent_to_register) { fiscalSum += line; fiscalCount += 1; }
      else { cashOnlySum += line; cashOnlyCount += 1; }
    }
    return {
      fiscalSum: Math.round(fiscalSum * 100) / 100,
      cashOnlySum: Math.round(cashOnlySum * 100) / 100,
      fiscalCount,
      cashOnlyCount,
    };
  }, [openTable, paymentPartialQty]);

  const closeTableNow = async () => {
    if (!openTable) return;
    setBusy(true);
    try {
      const r = await http.post("/ftext/restaurant-close", {
        table_nr: openTable.device_number,
        table_name: openTable.name,
        payments: closePayments.length
          ? closePayments.map((p) => ({
              payment_id: Number(p.payment_id),
              amount: p.amount ? Number(p.amount) : null,
            }))
          : [],
      });
      if (r.data?.ok) {
        // Wipe the persistent session on the server BEFORE celebrating;
        // otherwise an SSE refresh could repaint the table as occupied
        // moments after we've shown the success overlay.
        try {
          await http.post(`/tables/${openTable.id}/clear-session`);
        } catch (e) {
          // Don't block the close UX, but warn so the operator knows.
          toast.error("Η απόδειξη εκδόθηκε αλλά το τραπέζι δεν εκκαθαρίστηκε — ελέγξτε χειροκίνητα.");
        }
        toastFiscalOk();
        setCloseDlgOpen(false);
        setClosePayments([]);
        setCloseComment("");
        setCloseNonFiscal(false);
        setOpenTableId(null);
        setView("tables");
      } else {
        toastFiscalFail();
      }
    } catch {
      toastFiscalFail();
    } finally { setBusy(false); }
  };

  // Lightweight wrapper that the PaymentDialogBody can call when the
  // operator hits «Εκτύπωση Λογαριασμού» inside the payment dialog.
  const paymentDialogPrintProforma = async () => {
    if (!openTableId) return;
    try {
      const r = await http.post(`/tables/${openTableId}/print-proforma`);
      const total = Number(r.data?.total || 0);
      toast.success(`🖨️ Λογαριασμός · ${total.toFixed(2)} €`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία εκτύπωσης");
    }
  };

  // ---------- «Εργασίες» handlers ----------
  // Note: desktop disables Μεταφορά/Διαγραφή when there are unsent open_items.
  // Same gating applies here — see disabled props on the menu buttons.
  const tasksProforma = async () => {
    if (!openTableId) return;
    setBusy(true);
    try {
      const r = await http.post(`/tables/${openTableId}/print-proforma`);
      const printer = r.data?.printer_name || "";
      const total = Number(r.data?.total || 0);
      toast.success(`🖨️ Προφόρμα${printer ? ` στο ${printer}` : ""}${total ? ` · ${total.toFixed(2)} €` : ""}`);
      setTasksDlgOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία εκτύπωσης");
    } finally { setBusy(false); }
  };
  const tasksDelete = async (adminCode) => {
    if (!openTableId) return;
    setBusy(true);
    try {
      await http.post(
        `/tables/${openTableId}/delete-table`,
        adminCode ? { admin_code: adminCode } : {},
      );
      toast.success(`Διαγραφή τραπεζιού ${openTable?.name || ""} ✓`);
      setDeleteConfirmOpen(false);
      setDeleteForceOpen(false);
      setDeleteForceCode("");
      setTasksDlgOpen(false);
      setOpenTableId(null);
      setView("tables");
    } catch (e) {
      if (e.response?.status === 428) {
        // Τραπέζι σταλμένο στην ταμειακή — απαιτείται κωδικός διαχειριστή.
        setDeleteConfirmOpen(false);
        setDeleteForceCode("");
        setDeleteForceOpen(true);
        return;
      }
      toast.error(e.response?.data?.detail || "Αποτυχία διαγραφής");
    } finally { setBusy(false); }
  };

  // ---------- Μερική Είσπραξη — POST /tables/{id}/partial-payment ----------
  // Multi-stage flow mirroring desktop Restaurant.jsx:
  //   1) pick — operator selects items + qty
  //   2) warn — only when any selected item is still UNSENT: ask if we
  //             should also flush to the cash register, or keep the
  //             payment cash-only (skip ECR send).
  //   3) pay  — pick payment method(s); finalise via POST endpoint.
  const openPaymentPartial = () => {
    if (!openTable) return;
    setPaymentPartialQty(new Map());
    setPaymentPartialSplit([]);
    setPaymentPartialCashOnly(false);
    setPaymentPartialStage("pick");
    setPaymentPartialOpen(true);
  };

  // Sum (€) of currently picked items — drives the «pay» stage's total.
  const paymentPartialTotal = useMemo(() => {
    if (!openTable) return 0;
    let s = 0;
    for (const [key, qty] of paymentPartialQty.entries()) {
      const [bn, ii] = key.split(":").map(Number);
      const b = (openTable.submitted_batches || []).find((x) => x.batch_no === bn);
      const it = b?.items?.[ii];
      if (!it) continue;
      s += Number(qty || 0) * (Number(it.price || 0) + Number(it.modifier_total || 0));
    }
    return Math.round(s * 100) / 100;
  }, [openTable, paymentPartialQty]);

  // Stage 1 → 2/3 transition.
  const confirmPartialPick = () => {
    if (paymentPartialQty.size === 0) {
      toast.error("Επιλέξτε τουλάχιστον ένα είδος");
      return;
    }
    let hasUnsent = false;
    for (const key of paymentPartialQty.keys()) {
      const [bn, ii] = key.split(":").map(Number);
      const b = (openTable?.submitted_batches || []).find((x) => x.batch_no === bn);
      const it = b?.items?.[ii];
      if (it && !it.sent_to_register) { hasUnsent = true; break; }
    }
    if (hasUnsent) setPaymentPartialStage("warn");
    else { setPaymentPartialCashOnly(false); setPaymentPartialStage("pay"); }
  };

  // Final POST.
  const submitPaymentPartial = async () => {
    if (!openTableId) return;
    const selections = [];
    for (const [key, qty] of paymentPartialQty.entries()) {
      if (!qty || qty <= 0) continue;
      const [bn, ii] = key.split(":").map(Number);
      selections.push({ batch_no: bn, item_idx: ii, qty });
    }
    if (selections.length === 0) { toast.error("Επιλέξτε τουλάχιστον ένα είδος"); return; }
    const normalisedPayments = (paymentPartialSplit || [])
      .filter((p) => p && p.payment_id != null)
      .map((p) => {
        const amt = String(p.amount ?? "").trim().replace(",", ".");
        return { payment_id: Number(p.payment_id), amount: amt && Number.isFinite(Number(amt)) ? Number(amt) : null };
      });
    if (normalisedPayments.length === 0) { toast.error("Επιλέξτε τρόπο πληρωμής"); return; }
    const fixed = normalisedPayments.reduce((s, p) => s + (p.amount == null ? 0 : p.amount), 0);
    const nullCount = normalisedPayments.filter((p) => p.amount == null).length;
    if (nullCount === 0 && Math.abs(fixed - paymentPartialTotal) > 0.01) {
      toast.error(`Το ποσό δεν ταιριάζει: ${fixed.toFixed(2)} αντί για ${paymentPartialTotal.toFixed(2)}`);
      return;
    }
    if (nullCount > 0 && fixed > paymentPartialTotal + 0.01) { toast.error("Το άθροισμα ξεπερνά το σύνολο"); return; }
    setBusy(true);
    try {
      await http.post(`/tables/${openTableId}/partial-payment`, {
        selections,
        payments: normalisedPayments,
        comment: null,
        send_unsent_to_register: !paymentPartialCashOnly,
      });
      toastFiscalOk();
      setPaymentPartialOpen(false);
      setPaymentPartialQty(new Map());
      setPaymentPartialSplit([]);
      setPaymentPartialCashOnly(false);
      setPaymentPartialStage("pick");
      const fresh = await http.get("/tables");
      setTables(fresh.data || []);
      const t = (fresh.data || []).find((x) => x.id === openTableId);
      if (t && !tableHasSession(t)) {
        setCloseDlgOpen(false);
        setOpenTableId(null);
        setView("tables");
      }
    } catch {
      toastFiscalFail();
    } finally { setBusy(false); }
  };

  // ---------- Back navigation guard ----------
  // The «Πίσω» button: if there are unsubmitted lines (open_items) we
  // pop a confirmation; pressing «Ναι, απόρριψη» clears the open_items
  // server-side and returns to the tables view. With nothing pending,
  // navigates back immediately.
  const handleBack = () => {
    if (cart.length > 0) {
      setDiscardDlg(true);
      return;
    }
    setOpenTableId(null); setView("tables");
  };
  const confirmDiscard = async () => {
    setDiscardDlg(false);
    if (!openTableId) return;
    setBusy(true);
    try {
      // Feb 2026 — clear LOCAL cart first so the SSE preserve-local guard
      // in the onmessage handler doesn't restore the discarded items.
      const idToClear = openTableId;
      setTables((prev) =>
        prev.map((t) => (t.id === idToClear ? { ...t, open_items: [] } : t)),
      );
      await http.put(`/tables/${idToClear}/open-items`, { items: [] });
      toast.message("Απορρίφθηκαν τα μη σταλμένα είδη");
    } catch { /* offline tolerant */ }
    finally {
      setBusy(false);
      setOpenTableId(null);
      setView("tables");
    }
  };

  // ---------- Table transfer (μεταφορά τραπεζιού) ----------
  const performTransfer = async (dstId, force = false) => {
    if (!openTableId || !dstId || dstId === openTableId) return;
    setBusy(true);
    try {
      await http.post(`/tables/${openTableId}/transfer-to/${dstId}${force ? "?force=true" : ""}`);
      toast.success("Έγινε μεταφορά τραπεζιού");
      setTransferDlg(false);
      setOpenTableId(null);
      setView("tables");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.code === "DESTINATION_OCCUPIED") {
        // Ask for force-merge confirmation via toast w/ action.
        toast.error(d.message || "Το τραπέζι είναι κατειλημμένο", {
          duration: 8000,
          action: { label: "Συγχώνευση", onClick: () => performTransfer(dstId, true) },
        });
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "Αποτυχία μεταφοράς");
      }
    } finally { setBusy(false); }
  };

  // ---------- Submitted-item delete (pending correction, desktop parity) ----------
  // Toggle a pending-delete on a submitted item. Stays local until
  // «Καταχώρηση» flushes corrections. Re-tap to undo.
  const toggleDeleteSubmitted = (batchNo, itemIdx) => {
    const key = `${batchNo}:${itemIdx}`;
    setCorrections((prev) => {
      const cur = prev[key] || {};
      if (cur.deleted) {
        // Untoggle — drop the deletion flag (and the whole entry if
        // nothing else remains pending on it).
        const { deleted: _d, ...rest } = cur;
        if (Object.keys(rest).length === 0) {
          const { [key]: _r, ...without } = prev;
          return without;
        }
        return { ...prev, [key]: rest };
      }
      return { ...prev, [key]: { ...cur, deleted: true } };
    });
    setBatchActionDlg(null);
  };
  const cancelAllCorrections = () => setCorrections({});
  const _flushCorrections = async () => {
    if (!openTableId || Object.keys(corrections).length === 0) return null;
    const r = await http.post(`/tables/${openTableId}/apply-corrections`, { corrections });
    setCorrections({});
    // Refresh local table cache from server response.
    if (r?.data) {
      setTables((prev) => prev.map((t) => (t.id === openTableId ? r.data : t)));
      return r.data;
    }
    return null;
  };

  // ---------- Re-order: add a submitted item back into the cart ----------
  const reorderSubmittedItem = (item) => {
    if (!item || !openTableId) return;
    const lines = [...cart];
    lines.push({
      product_id: item.product_id,
      description: item.description,
      qty: 1,
      price: Number(item.price || 0),
      vat_rate: item.vat_rate,
      department: item.department,
      allow_comment: !!item.allow_comment,
      comment_group_ids: item.comment_group_ids || [],
      comments: item.comments ? [...item.comments] : [],
      comment: item.comment || "",
      free_text: item.free_text || null,
      modifier_total: Number(item.modifier_total || 0),
    });
    persistOpenItems(lines);
    toast.message("Προστέθηκε στο καλάθι");
    setBatchActionDlg(null);
  };

  // ---------- Derived ----------
  const correctionsCount = useMemo(() => Object.keys(corrections).length, [corrections]);
  const cartTotal = useMemo(
    () => cart.reduce(
      (s, it) => s + Number(it.qty || 1) * (Number(it.price || 0) + Number(it.modifier_total || 0)),
      0,
    ),
    [cart],
  );
  const hasSession = useMemo(() => tableHasSession(openTable), [openTable]);
  const filteredTables = useMemo(
    () => tables.filter((t) => {
      if (activeSection !== ALL_SECTIONS && t.section_id !== activeSection) return false;
      if (occupancyFilter === "free" && tableHasSession(t)) return false;
      if (occupancyFilter === "busy" && !tableHasSession(t)) return false;
      return true;
    }),
    [tables, activeSection, occupancyFilter],
  );
  // Counts (within the currently selected section) for the segmented
  // free/busy pills above the table grid — gives the waiter an at-a-
  // glance idea of how many tables are in each bucket.
  const sectionScopedTables = useMemo(
    () => tables.filter((t) => activeSection === ALL_SECTIONS || t.section_id === activeSection),
    [tables, activeSection],
  );
  const freeCount = useMemo(
    () => sectionScopedTables.filter((t) => !tableHasSession(t)).length,
    [sectionScopedTables],
  );
  const busyCount = sectionScopedTables.length - freeCount;
  // Per-section occupancy counts → small «· N» badge on each section
  // pill so the waiter sees at a glance which zone is busiest.
  const busyBySection = useMemo(() => {
    const map = new Map();
    let allBusy = 0;
    for (const t of tables) {
      if (!tableHasSession(t)) continue;
      allBusy += 1;
      if (t.section_id) map.set(t.section_id, (map.get(t.section_id) || 0) + 1);
    }
    map.set(ALL_SECTIONS, allBusy);
    return map;
  }, [tables]);
  // Sort state for the «Παραγγελίες» view. Field = "time" | "total",
  // dir = "desc" | "asc". Default: time desc (newest first).
  const [myordersSort, setMyordersSort] = useState({ field: "time", dir: "desc" });
  const cycleSort = (field) => setMyordersSort((s) => (
    s.field === field
      ? { field, dir: s.dir === "desc" ? "asc" : "desc" }
      : { field, dir: "desc" }
  ));
  const openTables = useMemo(() => {
    // «Παραγγελίες»: οι managers βλέπουν ΟΛΑ τα ανοιχτά τραπέζια· οι απλοί
    // χειριστές μόνο όσα άνοιξαν οι ίδιοι ή έχουν περάσει έστω μία παραγγελία.
    const isManager = user?.role === "manager" || user?.role === "super_manager";
    const list = tables.filter((t) => {
      if (!tableHasSession(t)) return false;
      if (isManager) return true;
      if (t.session_opened_by && t.session_opened_by === user?.id) return true;
      return (t.submitted_batches || []).some((b) => b.user_id === user?.id);
    });
    const mult = myordersSort.dir === "desc" ? -1 : 1;
    return list.sort((a, b) => {
      if (myordersSort.field === "total") {
        return (tableTotal(a) - tableTotal(b)) * mult;
      }
      const ta = a.session_opened_at ? new Date(a.session_opened_at).getTime() : 0;
      const tb = b.session_opened_at ? new Date(b.session_opened_at).getTime() : 0;
      return (ta - tb) * mult;
    });
  }, [tables, myordersSort, user]);
  const filteredProducts = useMemo(
    () => products.filter((p) => p.category_id === activeCat && !p.is_hidden && !p.sale_paused),
    [products, activeCat],
  );

  const elapsed = (iso) => {
    if (!iso) return "";
    const m = Math.floor((now - new Date(iso).getTime()) / 60000);
    if (!Number.isFinite(m) || m < 0) return "";
    return m < 60 ? `${m}'` : `${Math.floor(m / 60)}ω ${m % 60}'`;
  };

  // Re-position the platform «Made with Emergent» badge so it does not
  // overlap the bottom-strip buttons (Έξοδος / Λογαριασμός). We move it
  // to the top-left corner, shrink it hard, and drop opacity while this
  // layout is mounted. `pointer-events: none` guarantees it can never
  // intercept a tap regardless of its visual position. Original style
  // is restored on unmount.
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (!badge) return;
    const prev = badge.getAttribute("style") || "";
    badge.style.cssText = prev
      + ";bottom:auto !important;right:auto !important;top:2px !important;left:2px !important;"
      + "opacity:0.18 !important;pointer-events:none !important;"
      + "transform:scale(0.55) !important;transform-origin:top left !important;";
    return () => { badge.setAttribute("style", prev); };
  }, []);

  // ---------- RENDER ----------
  return (
    <div className="fixed inset-0 bg-slate-50 flex flex-col" data-testid="mobile-restaurant">
      {/* Top bar (hidden στο σερβίρισμα — έχει δικό του header) */}
      {view !== "serving" && (
      <div className="h-14 shrink-0 bg-white border-b border-slate-200 flex items-center px-3 gap-2">
        {view === "order" ? (
          <Button
            size="sm"
            variant={cart.length > 0 ? "destructive" : "ghost"}
            onClick={handleBack}
            data-testid="mr-back"
            className={cart.length > 0 ? "bg-red-600 hover:bg-red-700 text-white" : ""}
          >
            <ChevronLeft size={20} /> Πίσω
          </Button>
        ) : null}
        <div className="flex-1 font-bold text-base truncate">
          {view === "tables" && "ΤΡΑΠΕΖΙΑ"}
          {view === "myorders" && "ΟΙ ΠΑΡΑΓΓΕΛΙΕΣ ΜΟΥ"}
          {view === "serving" && "ΣΕΡΒΙΡΙΣΜΑ"}
          {view === "order" && openTable && (
            <>
              {openTable.name || `Τραπέζι ${openTable.position}`}
              <span className="text-xs text-slate-500 ml-2">
                · {sections.find((s) => s.id === openTable.section_id)?.name || ""}
              </span>
            </>
          )}
        </div>
        {view === "order" && openTable && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setTasksDlgOpen(true)}
            data-testid="mr-tasks-open"
            className="text-xs"
          >
            <Wrench size={14} className="mr-1" /> Εργασίες
          </Button>
        )}
        {/* Feb 2026 — User name γίνεται clickable για online operators + send message */}
        <button
          onClick={() => setOperatorsOpen(true)}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition"
          data-testid="mr-user-tap"
          title="Ενεργοί χειριστές"
        >
          <UsersRound size={12} className="text-emerald-600" />
          <span className="font-mono font-bold">{user?.name}</span>
        </button>
      </div>
      )}

      {/* Main */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {view === "tables" && (
          <>
            {/* Occupancy filter — 2 segmented pills, scoped to current section.
                Tap an active pill again to clear (= show all). */}
            <div className="px-2 pt-2 pb-1.5 flex gap-2 shrink-0 bg-white border-b border-slate-100">
              <button
                onClick={() => setOccupancyFilter((v) => (v === "free" ? null : "free"))}
                className={`flex-1 h-11 rounded-lg text-sm font-extrabold border-2 active:scale-[0.97] transition-all flex items-center justify-center gap-1.5 ${
                  occupancyFilter === "free"
                    ? "bg-emerald-600 text-white border-emerald-700 shadow-md"
                    : "bg-emerald-50 text-emerald-800 border-emerald-300"
                }`}
                data-testid="mr-occ-free"
              >
                <span className={`w-2 h-2 rounded-full ${occupancyFilter === "free" ? "bg-white" : "bg-emerald-500"}`} />
                ΕΛΕΥΘΕΡΑ
                <span className={`px-1.5 py-0.5 rounded-md text-[11px] font-mono ${
                  occupancyFilter === "free" ? "bg-white text-emerald-800" : "bg-white/70 text-emerald-700"
                }`}>{freeCount}</span>
              </button>
              <button
                onClick={() => setOccupancyFilter((v) => (v === "busy" ? null : "busy"))}
                className={`flex-1 h-11 rounded-lg text-sm font-extrabold border-2 active:scale-[0.97] transition-all flex items-center justify-center gap-1.5 ${
                  occupancyFilter === "busy"
                    ? "bg-red-600 text-white border-red-700 shadow-md"
                    : "bg-red-50 text-red-800 border-red-300"
                }`}
                data-testid="mr-occ-busy"
              >
                <span className={`w-2 h-2 rounded-full ${occupancyFilter === "busy" ? "bg-white" : "bg-red-500"}`} />
                ΣΕ ΧΡΗΣΗ
                <span className={`px-1.5 py-0.5 rounded-md text-[11px] font-mono ${
                  occupancyFilter === "busy" ? "bg-white text-red-800" : "bg-white/70 text-red-700"
                }`}>{busyCount}</span>
              </button>
            </div>
            <div className="px-2 py-2 flex gap-1.5 overflow-x-auto shrink-0 bg-white border-b border-slate-200">
              <button
                onClick={() => { setActiveSection(ALL_SECTIONS); setOccupancyFilter(null); }}
                className={`px-3 py-1.5 rounded-full text-sm font-bold whitespace-nowrap flex items-center gap-1.5 ${
                  activeSection === ALL_SECTIONS ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-700"
                }`}
                data-testid="mr-section-all"
              >
                <span>Όλα</span>
                {busyBySection.get(ALL_SECTIONS) > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono leading-none ${
                    activeSection === ALL_SECTIONS ? "bg-white text-blue-700" : "bg-red-500 text-white"
                  }`}>{busyBySection.get(ALL_SECTIONS)}</span>
                )}
              </button>
              {sections.map((s) => {
                const n = busyBySection.get(s.id) || 0;
                const isHot = n > 5;
                return (
                  <button
                    key={s.id}
                    onClick={() => { setActiveSection(s.id); setOccupancyFilter(null); }}
                    className={`relative px-3 py-1.5 rounded-full text-sm font-bold whitespace-nowrap flex items-center gap-1.5 ${
                      activeSection === s.id ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-700"
                    }`}
                    data-testid={`mr-section-${s.id}`}
                  >
                    <span>{s.name}</span>
                    {n > 0 && (
                      <span className="relative inline-flex">
                        {isHot && activeSection !== s.id && (
                          <span
                            className="absolute inset-0 rounded-md bg-red-500 animate-ping opacity-60"
                            aria-hidden
                          />
                        )}
                        <span className={`relative px-1.5 py-0.5 rounded-md text-[10px] font-mono leading-none ${
                          activeSection === s.id ? "bg-white text-blue-700" : "bg-red-500 text-white"
                        }`} data-testid={`mr-section-badge-${s.id}`}>{n}</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 overflow-y-auto p-2 grid grid-cols-3 gap-2 content-start auto-rows-[7.5rem]">
              {filteredTables.map((t) => {
                const total = tableTotal(t);
                const occ = tableHasSession(t);
                const elapsedMin = t.session_opened_at
                  ? Math.floor((now - new Date(t.session_opened_at).getTime()) / 60000)
                  : 0;
                const warn = occ && elapsedMin > 40;
                return (
                  <button
                    key={t.id}
                    onClick={() => openTableOrder(t)}
                    disabled={busy}
                    className={`relative rounded-lg border-2 flex flex-col items-stretch p-1 active:scale-95 transition-transform overflow-hidden shadow-sm ${
                      !occ ? "bg-emerald-200 border-emerald-600"
                        : warn ? "bg-amber-300 border-amber-600"
                          : "bg-red-300 border-red-600"
                    } ${recentlyUsedTableId === t.id ? "ring-4 ring-yellow-400 animate-pulse" : ""}`}
                    data-testid={`mr-table-${t.id}`}
                  >
                    {/* Centered NAME (wraps to up-to-2 lines).
                        Fallback to «Τραπέζι N» when no custom name is set.
                        pointer-events-none on inner contents so the parent
                        button is the unambiguous click target. */}
                    <div className="flex-1 flex items-center justify-center px-0.5 min-h-0 pointer-events-none">
                      <span className={`text-sm font-extrabold text-center leading-tight line-clamp-2 break-words ${
                        !occ ? "text-emerald-950" : warn ? "text-amber-950" : "text-red-950"
                      }`}
                            data-testid={`mr-table-name-${t.id}`}>
                        {t.name || `Τραπέζι ${t.position}`}
                      </span>
                    </div>
                    {/* Bigger total in the middle row for occupied tables */}
                    {occ ? (
                      <div className={`text-center font-mono font-extrabold text-base leading-none mb-0.5 pointer-events-none ${
                        warn ? "text-amber-950" : "text-red-950"
                      }`}
                           data-testid={`mr-table-total-${t.id}`}>
                        {fmtEUR(total)}
                      </div>
                    ) : (
                      <div className="text-center text-[10px] uppercase text-emerald-900 font-extrabold mb-0.5 pointer-events-none tracking-wider">ΚΕΝΟ</div>
                    )}
                    {/* Bottom row: position (left) · open time (right) — same font as name. */}
                    <div className={`flex items-end justify-between text-[10px] font-bold leading-none mt-auto pointer-events-none ${
                      !occ ? "text-emerald-900" : warn ? "text-amber-900" : "text-red-900"
                    }`}>
                      <span className="font-mono">{t.position || ""}</span>
                      {occ ? (
                        <span>{elapsed(t.session_opened_at)}{warn ? " ⚠" : ""}</span>
                      ) : (
                        <span className="opacity-0">—</span>
                      )}
                    </div>
                  </button>
                );
              })}
              {filteredTables.length === 0 && (
                <div className="col-span-3 text-center text-slate-400 py-8">Κανένα τραπέζι</div>
              )}
            </div>
          </>
        )}

        {view === "myorders" && (
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {/* Sort controls — 2 fields (Ώρα / Σύνολο), tap active again to flip dir */}
            <div className="bg-white border-2 border-slate-200 rounded-lg px-2 py-2 shadow-sm">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs text-slate-600">
                  <span className="font-bold text-slate-800">{openTables.length}</span> ενεργές
                </div>
                <div className="text-[10px] uppercase text-slate-400 tracking-wider font-bold">Ταξινόμηση</div>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => cycleSort("time")}
                  className={`h-9 rounded-md text-xs font-bold flex items-center justify-center gap-1.5 border-2 active:scale-[0.97] ${
                    myordersSort.field === "time"
                      ? "bg-blue-600 text-white border-blue-700"
                      : "bg-white text-slate-700 border-slate-300"
                  }`}
                  data-testid="mr-myorders-sort-time"
                >
                  <Clock size={13} />
                  <span>Ώρα</span>
                  {myordersSort.field === "time" && (
                    myordersSort.dir === "desc" ? <ArrowDownAZ size={13} /> : <ArrowUpAZ size={13} />
                  )}
                </button>
                <button
                  onClick={() => cycleSort("total")}
                  className={`h-9 rounded-md text-xs font-bold flex items-center justify-center gap-1.5 border-2 active:scale-[0.97] ${
                    myordersSort.field === "total"
                      ? "bg-blue-600 text-white border-blue-700"
                      : "bg-white text-slate-700 border-slate-300"
                  }`}
                  data-testid="mr-myorders-sort-total"
                >
                  <Receipt size={13} />
                  <span>Σύνολο</span>
                  {myordersSort.field === "total" && (
                    myordersSort.dir === "desc" ? <ArrowDownAZ size={13} /> : <ArrowUpAZ size={13} />
                  )}
                </button>
              </div>
            </div>
            {openTables.length === 0 && (
              <div className="text-center text-slate-400 py-12" data-testid="mr-myorders-empty">Καμία ενεργή παραγγελία</div>
            )}
            {openTables.map((t) => {
              const total = tableTotal(t);
              const elapsedMin = t.session_opened_at
                ? Math.floor((now - new Date(t.session_opened_at).getTime()) / 60000)
                : 0;
              const warn = elapsedMin > 40;
              return (
                <button
                  key={t.id}
                  onClick={() => openTableOrder(t)}
                  className={`w-full rounded-lg p-3 text-left active:bg-slate-50 border-2 ${
                    warn ? "bg-amber-50 border-amber-400" : "bg-white border-slate-200"
                  }`}
                  data-testid={`mr-myorder-${t.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold" data-testid={`mr-myorder-name-${t.id}`}>
                      {t.name || `Τραπέζι ${t.position}`}
                      <span className="text-xs text-slate-500 ml-1.5 font-normal">
                        · {sections.find((s) => s.id === t.section_id)?.name || ""}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock size={11} />{elapsed(t.session_opened_at)}{warn ? " ⚠" : ""}
                    </div>
                  </div>
                  <div className="font-mono font-bold text-red-700 text-lg">{fmtEUR(total)}</div>
                </button>
              );
            })}
          </div>
        )}

        {view === "order" && openTable && (
          <>
            {/* Previous batches — collapsed by default, tap header to expand.
                Read-only history; tap an item (when open) to fire the action
                menu (Επαναπαραγγελία / Διαγραφή). */}
            {(openTable.submitted_batches || []).some((b) => (b.items || []).some((it) => !it.corrected_deleted && !it.paid_partial)) && (() => {
              const prevRows = (openTable.submitted_batches || []).flatMap((b) =>
                (b.items || [])
                  .map((it, i) => ({ b, i, it }))
                  .filter(({ it }) => !it.corrected_deleted && !it.paid_partial),
              );
              const prevTotal = prevRows.reduce(
                (s, { it }) => s + Number(it.qty || 0) * (Number(it.price || 0) + Number(it.modifier_total || 0)),
                0,
              );
              return (
                <div className="shrink-0 bg-amber-50 border-b border-amber-200 max-h-[28vh] overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => setPrevOpen((v) => !v)}
                    className="w-full px-3 py-1.5 flex items-center gap-1.5 bg-amber-100 text-amber-900 active:bg-amber-200"
                    data-testid="mr-prev-toggle"
                    aria-expanded={prevOpen}
                  >
                    <History size={13} />
                    <span className="text-xs font-bold uppercase">Προηγούμενες παραγγελίες</span>
                    <span className="ml-1 px-1.5 rounded-full bg-amber-200 text-[10px] font-mono">{prevRows.length}</span>
                    <span className="ml-auto text-[11px] font-mono font-bold">{fmtEUR(prevTotal)}</span>
                    <span className="ml-2 text-xs">{prevOpen ? "▲" : "▼"}</span>
                  </button>
                  {prevOpen && prevRows.map(({ b, i, it }) => {
                    const isFiscal = !!it.sent_to_register;
                    const corrKey = `${b.batch_no}:${i}`;
                    const pendingDeleted = !!corrections[corrKey]?.deleted;
                    return (
                      <button
                        key={corrKey}
                        onClick={() => setBatchActionDlg({ batchNo: b.batch_no, itemIdx: i, item: it, isFiscal, pendingDeleted })}
                        className={`w-full px-3 py-1.5 border-b border-amber-100 text-left flex items-center gap-2 active:bg-amber-100 ${pendingDeleted ? "bg-rose-50 line-through opacity-60" : ""}`}
                        data-testid={`mr-batch-${b.batch_no}-${i}`}
                      >
                        <span className="text-[10px] text-amber-700 font-mono w-6 shrink-0">#{b.batch_no}</span>
                        <span className="flex-1 truncate text-sm font-semibold">{it.description}</span>
                        {pendingDeleted && <span className="text-[9px] text-rose-700 font-bold uppercase">διαγρ.</span>}
                        {isFiscal && !pendingDeleted && <span className="text-[9px] text-emerald-700 font-bold uppercase">τ</span>}
                        <span className="text-[11px] text-slate-700 font-mono shrink-0">×{it.qty}</span>
                        <span className="text-[11px] font-mono font-bold text-slate-800 shrink-0">
                          {(Number(it.qty || 0) * (Number(it.price || 0) + Number(it.modifier_total || 0))).toFixed(2)} €
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {/* Cart — capped at 25vh so the products grid below always
                keeps room for at least ~3 rows of items. Scroll inside
                the cart when it overflows. */}
            <div className="shrink-0 bg-white border-b border-slate-200 max-h-[25vh] overflow-y-auto">
              <div className="px-3 py-1 flex justify-between items-center bg-slate-100">
                <span className="text-xs font-bold uppercase">Καλάθι ({cart.length})</span>
                <span className="font-mono font-bold text-emerald-700">{fmtEUR(cartTotal)}</span>
              </div>
              {cart.length === 0 ? (
                <div className="text-center text-slate-400 text-sm py-2 italic">Κενό</div>
              ) : (
                cart.map((it, i) => {
                  const canComment = !!it.allow_comment || (it.comment_group_ids && it.comment_group_ids.length > 0);
                  return (
                    <div key={i} className="px-3 py-1 border-b border-slate-100" data-testid={`mr-cart-line-${i}`}>
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate leading-tight">{it.description}</div>
                          <div className="text-[11px] text-slate-500 font-mono leading-tight">
                            {Number(it.price || 0).toFixed(2)} €
                            {Number(it.modifier_total || 0) !== 0 && (
                              <span className={it.modifier_total > 0 ? "text-emerald-700" : "text-red-700"}>
                                {" "}({it.modifier_total > 0 ? "+" : ""}{Number(it.modifier_total).toFixed(2)} €)
                              </span>
                            )}
                            {" "}× {it.qty}
                          </div>
                        </div>
                        {canComment && (
                          <button
                            onClick={() => openCommentFor(i)}
                            className="w-7 h-7 rounded text-blue-600 active:bg-blue-50"
                            data-testid={`mr-cart-comment-${i}`}
                            aria-label="Σχόλια"
                          ><Pencil size={13} className="mx-auto" /></button>
                        )}
                        <button onClick={() => incLine(i, -1)} className="w-7 h-7 rounded bg-slate-200 active:bg-slate-300"><Minus size={13} className="mx-auto" /></button>
                        <span className="w-5 text-center font-bold text-sm">{it.qty}</span>
                        <button onClick={() => incLine(i, 1)} className="w-7 h-7 rounded bg-slate-200 active:bg-slate-300"><Plus size={13} className="mx-auto" /></button>
                        <button onClick={() => removeLine(i)} className="w-7 h-7 rounded text-red-600 active:bg-red-50"><Trash2 size={13} className="mx-auto" /></button>
                      </div>
                      {(it.comment || it.free_text) && (
                        <div className="text-[10px] text-slate-600 italic truncate leading-tight" data-testid={`mr-cart-line-comment-${i}`}>
                          {it.comment || it.free_text}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {/* Category pills */}
            <div className="shrink-0 px-2 py-2 flex gap-1.5 overflow-x-auto bg-white border-b border-slate-200">
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCat(c.id)}
                  className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                    activeCat === c.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                  data-testid={`mr-cat-${c.id}`}
                >{c.name}</button>
              ))}
            </div>
            {/* Products — tighter card padding + smaller min-height so
                more rows fit when many products are visible. */}
            <div className="flex-1 overflow-y-auto p-1.5 grid grid-cols-2 gap-1.5 content-start">
              {filteredProducts.map((p) => {
                const sb = settings?.modules_stock === false ? null : stockBadge(p, settings?.stock_low_threshold ?? 5);
                return (
                <button
                  key={p.id}
                  onClick={() => addProduct(p)}
                  className="relative bg-white border-2 border-slate-200 active:border-emerald-500 active:bg-emerald-50 rounded-lg px-2 py-1.5 text-left"
                  data-testid={`mr-product-${p.id}`}
                >
                  <div className="font-bold text-[13px] text-slate-800 line-clamp-2 leading-tight min-h-[2em]">{p.description}</div>
                  <div className="font-mono text-emerald-700 font-bold text-sm leading-tight">{fmtEUR(p.price)}</div>
                  {sb && (
                    <div className={`absolute bottom-1 right-1 min-w-[22px] px-1 py-0.5 rounded text-[10px] font-mono font-bold text-center shadow ${sb.cls}`} data-testid={`mr-stock-badge-${p.id}`}>
                      {sb.qty}
                    </div>
                  )}
                </button>
                );
              })}
              {filteredProducts.length === 0 && (
                <div className="col-span-2 text-center text-slate-400 py-8">Κανένα προϊόν</div>
              )}
            </div>
            {/* Action bar */}
            <div className="shrink-0 bg-white border-t-2 border-slate-300 p-2 grid grid-cols-2 gap-2">
              <Button
                onClick={openSubmitChoice}
                disabled={busy || (cart.length === 0 && correctionsCount === 0 && !(openTable && allUnsentSelections(openTable).length > 0))}
                className="h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold relative"
                data-testid="mr-send-kitchen"
              >
                <Send size={16} className="mr-1.5" /> Καταχώρηση
                {correctionsCount > 0 && (
                  <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-300 text-amber-900 text-[11px] font-bold" data-testid="mr-corrections-badge">
                    <Pencil size={10} /> {correctionsCount}
                  </span>
                )}
              </Button>
              <Button
                onClick={onApodeixiClick}
                disabled={
                  busy
                  // Disable when cart has uncommitted items (must press Καταχώρηση first) — desktop parity.
                  || cart.length > 0
                  // Disable while pending corrections exist (same reason).
                  || correctionsCount > 0
                  // Disable when there's literally nothing to pay.
                  || !(openTable && (
                    (openTable.submitted_batches || []).some((b) =>
                      (b.items || []).some((it) => !it.corrected_deleted && !it.paid_partial),
                    )
                  ))
                }
                title={cart.length > 0 || correctionsCount > 0 ? "Καταχωρήστε πρώτα τις αλλαγές πριν το κλείσιμο" : undefined}
                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-60"
                data-testid="mr-print-bill"
              ><Receipt size={16} className="mr-1.5" /> Απόδειξη</Button>
            </div>
          </>
        )}
        {view === "serving" && settings?.modules_serving !== false && (
          <ServingScreen onClose={() => setView("tables")} compact waiterName={user?.name} waiterId={user?.id} />
        )}
      </div>

      {/* Bottom nav (hidden inside order view) */}
      {view !== "order" && (
        <div className={`h-16 shrink-0 bg-white border-t-2 border-slate-200 grid ${settings?.modules_serving !== false ? "grid-cols-4" : "grid-cols-3"}`}>
          <button
            onClick={() => setView("tables")}
            className={`flex flex-col items-center justify-center gap-0.5 ${view === "tables" ? "text-blue-700 font-bold" : "text-slate-600"}`}
            data-testid="mr-nav-tables"
          >
            <Utensils size={20} />
            <span className="text-[11px]">Τραπέζια</span>
          </button>
          {settings?.modules_serving !== false && (
            <button
              onClick={() => setView("serving")}
              className={`flex flex-col items-center justify-center gap-0.5 ${view === "serving" ? "text-indigo-700 font-bold" : "text-slate-600"}`}
              data-testid="mr-nav-serving"
            >
              <ConciergeBell size={20} />
              <span className="text-[11px]">Σερβίρισμα</span>
            </button>
          )}
          <button
            onClick={() => setView("myorders")}
            className={`flex flex-col items-center justify-center gap-0.5 ${view === "myorders" ? "text-blue-700 font-bold" : "text-slate-600"}`}
            data-testid="mr-nav-myorders"
          >
            <ClipboardList size={20} />
            <span className="text-[11px]">Παραγγελίες</span>
          </button>
          <button
            onClick={() => { logout(); }}
            className="flex flex-col items-center justify-center gap-0.5 text-red-600"
            data-testid="mr-nav-logout"
          >
            <LogOut size={20} />
            <span className="text-[11px]">Έξοδος</span>
          </button>
        </div>
      )}

      {/* Feb 2026 — Ενεργοί Χειριστές + Send Message (bottom sheet) */}
      <Dialog open={operatorsOpen} onOpenChange={setOperatorsOpen}>
        <DialogContent className="max-w-md p-0 gap-0 max-h-[85vh] flex flex-col" data-testid="mr-operators-sheet">
          <DialogHeader className="px-4 py-3 border-b shrink-0 bg-emerald-50">
            <DialogTitle className="text-base flex items-center gap-2">
              <UsersRound size={18} className="text-emerald-700" />
              Ενεργοί Χειριστές
              <span className="ml-auto text-xs font-mono text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded">
                {onlineOperators.length}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-2">
            {onlineOperators.length === 0 && (
              <div className="text-center text-slate-500 text-sm py-8">
                Δεν υπάρχουν άλλοι ενεργοί χειριστές αυτή τη στιγμή.
              </div>
            )}
            {onlineOperators.map((op) => {
              const isSelf = op.user_id === user?.id;
              return (
                <div
                  key={op.user_id}
                  className={`flex items-center gap-3 p-3 border-b border-slate-100 ${isSelf ? "bg-blue-50" : ""}`}
                  data-testid={`mr-op-row-${op.user_id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-emerald-500 grid place-items-center text-white font-bold text-sm shrink-0">
                    {(op.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">
                      {op.name}
                      {isSelf && <span className="ml-2 text-[10px] text-blue-700">(εγώ)</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide">
                      {op.role} · {op.on_shift ? "🟢 βάρδια" : "off shift"}
                    </div>
                  </div>
                  {!isSelf && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setMsgTarget(op); setOperatorsOpen(false); }}
                      data-testid={`mr-op-msg-${op.user_id}`}
                      className="text-xs h-8 px-2 gap-1"
                    >
                      <MessageSquare size={13} /> Μήνυμα
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Message dialog (shared με desktop badge) */}
      <SendMessageDialog
        open={!!msgTarget}
        onOpenChange={(v) => { if (!v) setMsgTarget(null); }}
        target={msgTarget}
      />

      {/* Comment dialog */}
      <Dialog open={cmtDlg.open} onOpenChange={(v) => !v && setCmtDlg({ open: false, idx: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" })}>
        <DialogContent className="max-w-md p-0 gap-0 max-h-[85vh] flex flex-col" data-testid="mr-comment-dialog">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="text-base">
              Σχόλια — {cmtLine?.description || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {cmtGroupsForLine.length === 0 && (
              <div className="text-xs text-slate-500 italic">Καμία προεπιλεγμένη λίστα σχολίων — χρησιμοποιήστε το πεδίο ελεύθερου κειμένου.</div>
            )}
            {/* Προθέματα σχολίων — οπλίζεις ένα και εφαρμόζεται στο επόμενο σχόλιο */}
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="mr-cmt-prefix-bar">
              {MOBILE_PREFIXES.map((p) => {
                const active = cmtDlg.activePrefix === p;
                return (
                  <button
                    key={p}
                    onClick={() => togglePrefix(p)}
                    data-testid={`mr-cmt-prefix-${p}`}
                    className={`shrink-0 px-3 h-8 rounded-full text-xs font-extrabold uppercase tracking-wide border-2 transition-all active:scale-95 ${
                      active
                        ? "bg-red-600 text-white border-red-700 ring-2 ring-red-300"
                        : p === "EXTRA"
                          ? "bg-white border-red-400 text-red-600"
                          : "bg-white border-slate-300 text-slate-700"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            {cmtDlg.activePrefix && (
              <div className="text-[11px] font-bold text-red-600" data-testid="mr-cmt-prefix-armed">
                ▶ Πρόθεμα: {cmtDlg.activePrefix} — πάτησε το σχόλιο που θα το πάρει
              </div>
            )}
            {cmtGroupsForLine.map((g) => (
              <div key={g.id} className="space-y-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-300 pb-1">
                  {g.name}
                </div>
                {g.buckets.map((b) => {
                  // Each subgroup (radio_group) renders as a tinted card
                  // with its own subheader so it ξεχωρίζει visually from
                  // sibling sub-groups within the same comment-group.
                  const isPlain = b.key === "__plain__";
                  return (
                    <div
                      key={b.key}
                      className={
                        isPlain
                          ? ""
                          : "rounded-lg bg-slate-50 border border-slate-200 p-2 space-y-1.5"
                      }
                      data-testid={`mr-cmt-bucket-${b.key}`}
                    >
                      {!isPlain && (
                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-slate-500">
                          <span className="inline-block w-1 h-3 rounded bg-blue-500" />
                          <span className="tracking-wide">{b.label}</span>
                          <span className="text-[9px] font-normal text-slate-400 normal-case ml-auto">
                            {b.multiple ? "πολλαπλή επιλογή" : "μία επιλογή"}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {b.items.map((c) => {
                          const qty = cmtDlg.selections[c.id] || 0;
                          const on = qty !== 0;
                          const cpref = cmtDlg.prefixes?.[c.id] || null;
                          const nameWithPref = cpref ? `${cpref} ${c.name}` : c.name;
                          const baseLabel =
                            qty === -1 ? `ΧΩΡΙΣ ${c.name}`
                              : qty > 1 ? `${qty}× ${nameWithPref}`
                                : c.is_deduction && !cpref ? `ΧΩΡΙΣ ${c.name}` : nameWithPref;
                          const tint = on && c.color ? c.color : null;
                          if (c.splittable) {
                            // 3-section splittable tile: − / qty × name / +
                            return (
                              <div
                                key={c.id}
                                className={`rounded-full overflow-hidden inline-flex items-stretch border-2 text-sm font-bold ${
                                  on ? "border-blue-600 shadow-md" : "border-slate-300"
                                }`}
                                style={on ? { borderColor: tint || "#2563eb" } : undefined}
                                data-testid={`mr-cmt-chip-${c.id}`}
                              >
                                <button
                                  onClick={() => decSplit(c)}
                                  disabled={qty === -1}
                                  className="px-2.5 bg-black/10 active:bg-black/20 disabled:opacity-40"
                                  data-testid={`mr-cmt-chip-${c.id}-minus`}
                                  aria-label="−"
                                >−</button>
                                <div
                                  className="px-2.5 py-1 grid place-items-center text-center"
                                  style={
                                    on
                                      ? { backgroundColor: tint || "#2563eb", color: "#fff" }
                                      : { backgroundColor: "#f1f5f9", color: "#1e293b" }
                                  }
                                >
                                  <span>{baseLabel}</span>
                                  {qty !== 0 && Number(c.price_delta || 0) !== 0 && (
                                    <span className="block text-[9px] opacity-90">
                                      {qty === -1 || c.is_deduction ? "−" : "+"}
                                      {(Number(c.price_delta) * Math.abs(qty === -1 ? 1 : qty)).toFixed(2)}€
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => incSplit(c)}
                                  className="px-2.5 bg-black/10 active:bg-black/20"
                                  data-testid={`mr-cmt-chip-${c.id}-plus`}
                                  aria-label="+"
                                >+</button>
                              </div>
                            );
                          }
                          // Plain toggle / radio chip
                          const style = on && tint ? { backgroundColor: tint, borderColor: tint, color: "#fff" } : undefined;
                          return (
                            <button
                              key={c.id}
                              onClick={() => toggleCommentChip(c)}
                              className={`px-2.5 py-1 rounded-full text-sm border-2 inline-flex items-center gap-1 ${
                                on
                                  ? "bg-blue-600 border-blue-600 text-white"
                                  : "bg-white border-slate-300 text-slate-700"
                              }`}
                              style={style}
                              data-testid={`mr-cmt-chip-${c.id}`}
                            >
                              {on && <span aria-hidden className="text-[11px]">✓</span>}
                              {baseLabel}
                              {Number(c.price_delta || 0) !== 0 && (
                                <span className="text-[10px] opacity-80">
                                  {c.is_deduction ? "−" : "+"}{Number(c.price_delta).toFixed(2)}€
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 mb-1.5">Ελεύθερο κείμενο</div>
              <Textarea
                value={cmtDlg.freeText}
                onChange={(e) => setCmtDlg((d) => ({ ...d, freeText: e.target.value }))}
                placeholder="π.χ. χωρίς πάγο, καλά ψημένο…"
                className="min-h-[80px] text-sm"
                data-testid="mr-cmt-freetext"
              />
            </div>
          </div>
          <DialogFooter className="px-4 py-3 border-t shrink-0 grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={() => setCmtDlg({ open: false, idx: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" })}
              data-testid="mr-cmt-cancel"
            >Άκυρο</Button>
            <Button
              onClick={saveComment}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="mr-cmt-save"
            >Αποθήκευση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard-cart confirmation when going back with unsubmitted lines */}
      <Dialog open={discardDlg} onOpenChange={setDiscardDlg}>
        <DialogContent className="max-w-sm" data-testid="mr-discard-dialog">
          <DialogHeader>
            <DialogTitle>Απώλεια παραγγελίας</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700">
            Έχετε <b>{cart.length}</b> είδ{cart.length === 1 ? "ος" : "η"} που δεν έχουν σταλεί ακόμα στα παρασκευαστήρια.
            Θέλετε σίγουρα να τα απορρίψετε;
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => setDiscardDlg(false)} data-testid="mr-discard-cancel">Άκυρο</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDiscard}
              data-testid="mr-discard-confirm"
            >Ναι, απόρριψη</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table transfer */}
      <Dialog open={transferDlg} onOpenChange={setTransferDlg}>
        <DialogContent className="max-w-md p-0 gap-0 max-h-[85vh] flex flex-col" data-testid="mr-transfer-dialog">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="text-base">
              Μεταφορά από Τ{openTable?.position || openTable?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-3 py-3 grid grid-cols-3 gap-2 content-start">
            {tables
              .filter((t) => t.id !== openTableId)
              .map((t) => {
                const occ = tableHasSession(t);
                return (
                  <button
                    key={t.id}
                    onClick={() => performTransfer(t.id, false)}
                    disabled={busy}
                    className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center p-1 active:scale-95 ${
                      !occ ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"
                    }`}
                    data-testid={`mr-transfer-dst-${t.id}`}
                  >
                    <div className="text-xl font-bold text-slate-800">{t.position || t.name}</div>
                    <div className="text-[10px] text-slate-500 truncate w-full text-center">
                      {sections.find((s) => s.id === t.section_id)?.name || ""}
                    </div>
                    {occ && <div className="text-[10px] text-red-600 font-bold">{fmtEUR(tableTotal(t))}</div>}
                  </button>
                );
              })}
          </div>
          <DialogFooter className="px-4 py-3 border-t shrink-0">
            <Button variant="outline" onClick={() => setTransferDlg(false)} data-testid="mr-transfer-cancel">Άκυρο</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* «Εργασίες» — 4-action menu (Μερική Πληρωμή / Μεταφορά / Προφόρμα / Διαγραφή).
          Mirrors the desktop order-tasks-popover. Disabled gating matches:
          partial payment requires at least one paid-able submitted item;
          transfer & delete require no unsent open_items. */}
      <Dialog open={tasksDlgOpen} onOpenChange={setTasksDlgOpen}>
        <DialogContent className="max-w-sm p-0 gap-0" data-testid="mr-tasks-dialog">
          <DialogHeader className="px-4 py-3 border-b bg-blue-50">
            <DialogTitle className="text-base flex items-center gap-2 text-blue-900">
              <Wrench size={18} /> Εργασίες — {openTable?.name || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="p-3 grid grid-cols-2 gap-2">
            {(() => {
              // Gating mirrors desktop (Restaurant.jsx L2322-L2399).
              const hasOpenItems = (openTable?.open_items || []).length > 0;
              const hasPayable = (openTable?.submitted_batches || []).some((b) =>
                (b.items || []).some((it) => !it.corrected_deleted && !it.paid_partial),
              );
              const partialDisabled = busy || hasOpenItems || !hasPayable;
              const transferDisabled = busy || hasOpenItems;
              const proformaDisabled = busy || hasOpenItems || !hasPayable;
              const deleteDisabled = busy || hasOpenItems;
              return (
                <>
                  <Button
                    className="h-16 bg-violet-600 hover:bg-violet-700 text-white font-bold leading-tight whitespace-normal px-2 flex-col gap-0"
                    onClick={() => { setTasksDlgOpen(false); openPaymentPartial(); }}
                    disabled={partialDisabled}
                    data-testid="mr-tasks-partial-pay"
                  >
                    <HandCoins size={18} />
                    <span>Μερική Πληρωμή</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-16 border-2 border-slate-300 text-slate-700 font-bold leading-tight whitespace-normal px-2 flex-col gap-0"
                    onClick={() => { setTasksDlgOpen(false); setTransferDlg(true); }}
                    disabled={transferDisabled}
                    data-testid="mr-tasks-transfer"
                  >
                    <Move size={18} />
                    <span>Μεταφορά Τραπεζιού</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-16 border-2 border-blue-300 text-blue-700 font-bold leading-tight whitespace-normal px-2 flex-col gap-0"
                    onClick={tasksProforma}
                    disabled={proformaDisabled}
                    data-testid="mr-tasks-proforma"
                  >
                    <Printer size={18} />
                    <span>Εκτύπωση Προφόρμας</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-16 border-2 border-amber-300 text-amber-800 font-bold leading-tight whitespace-normal px-2 flex-col gap-0"
                    onClick={() => { setTasksDlgOpen(false); setQuickNoteOpen(true); }}
                    disabled={busy}
                    data-testid="mr-tasks-quicknote"
                  >
                    <Pencil size={18} />
                    <span>Σχόλια</span>
                  </Button>
                  <Button
                    className="h-16 bg-red-600 hover:bg-red-700 text-white font-bold leading-tight whitespace-normal px-2 flex-col gap-0"
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={deleteDisabled}
                    data-testid="mr-tasks-delete"
                  >
                    <Trash2 size={18} />
                    <span>Διαγραφή Τραπεζιού</span>
                  </Button>
                </>
              );
            })()}
          </div>
          <DialogFooter className="px-3 pb-3 pt-0 border-t bg-slate-50">
            <Button
              variant="outline"
              onClick={() => setTasksDlgOpen(false)}
              data-testid="mr-tasks-cancel"
              className="w-full h-11 mt-3"
            >Κλείσιμο</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Επιβεβαίωση διαγραφής τραπεζιού */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm" data-testid="mr-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base text-red-700">Διαγραφή Τραπεζιού</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700">
            Όλα τα δεδομένα του τραπεζιού «<b>{openTable?.name}</b>» θα διαγραφούν και η ενέργεια θα καταγραφεί στις διορθώσεις.
            Συνεχίζετε;
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} data-testid="mr-delete-confirm-cancel">Άκυρο</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => tasksDelete()}
              disabled={busy}
              data-testid="mr-delete-confirm-ok"
            >Διαγραφή</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force delete (τραπέζι σταλμένο στην ταμειακή) — κωδικός διαχειριστή */}
      <Dialog open={deleteForceOpen} onOpenChange={(o) => { if (!o) { setDeleteForceOpen(false); setDeleteForceCode(""); } }}>
        <DialogContent className="max-w-sm" data-testid="mr-delete-force-dialog">
          <DialogHeader>
            <DialogTitle className="text-base text-red-700">Διαγραφή Τραπεζιού «{openTable?.name}»</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 leading-relaxed">
            Η διαδικασία αυτή μπορεί να φέρει αστάθεια στο πρόγραμμα. Μετά την
            διαγραφή παρακαλώ ελέγξτε την κατάσταση του τραπεζιού στην ταμειακή.
            Για να ολοκληρωθεί η διαγραφή βάλτε τον κωδικό Διαχειριστή.
          </div>
          <div
            className="mt-2 rounded-lg border-2 border-slate-300 bg-slate-50 p-3 text-center font-mono text-2xl tracking-[0.4em] text-slate-800 select-none min-h-[48px] flex items-center justify-center"
            data-testid="mr-delete-force-code-display"
          >
            {deleteForceCode
              ? "•".repeat(deleteForceCode.length)
              : <span className="text-slate-400 text-base tracking-normal">Κωδικός Διαχειριστή</span>}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2" data-testid="mr-delete-force-numpad">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
              <button
                type="button"
                key={k}
                onClick={() => setDeleteForceCode((c) => (c + k).slice(0, 20))}
                data-testid={`mr-delete-force-key-${k}`}
                className="h-12 rounded-lg border-2 border-slate-300 bg-white active:scale-95 active:bg-slate-200 text-xl font-bold text-slate-800"
              >
                {k}
              </button>
            ))}
            <button type="button" onClick={() => setDeleteForceCode("")} data-testid="mr-delete-force-key-clear" className="h-12 rounded-lg border-2 border-amber-300 bg-amber-50 active:scale-95 text-xs font-bold text-amber-800">ΚΑΘΑΡ.</button>
            <button type="button" onClick={() => setDeleteForceCode((c) => (c + "0").slice(0, 20))} data-testid="mr-delete-force-key-0" className="h-12 rounded-lg border-2 border-slate-300 bg-white active:scale-95 active:bg-slate-200 text-xl font-bold text-slate-800">0</button>
            <button type="button" onClick={() => setDeleteForceCode((c) => c.slice(0, -1))} data-testid="mr-delete-force-key-backspace" className="h-12 rounded-lg border-2 border-slate-300 bg-slate-100 active:scale-95 text-base font-bold text-slate-700">⌫</button>
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => { setDeleteForceOpen(false); setDeleteForceCode(""); }} data-testid="mr-delete-force-cancel">Ακύρωση</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => tasksDelete(deleteForceCode)}
              disabled={busy || !deleteForceCode}
              data-testid="mr-delete-force-confirm"
            >Διαγραφή</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submitted-item action menu (re-order / delete) */}
      <Dialog open={!!batchActionDlg} onOpenChange={(v) => !v && setBatchActionDlg(null)}>
        <DialogContent className="max-w-sm" data-testid="mr-batch-action-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {batchActionDlg?.item?.description}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500">
            Παραγγελία #{batchActionDlg?.batchNo} · {batchActionDlg?.item?.qty} × {Number(batchActionDlg?.item?.price || 0).toFixed(2)} €
            {batchActionDlg?.isFiscal && !batchActionDlg?.pendingDeleted && (
              <div className="mt-1 text-amber-700">⚠ Έχει σταλεί στην ταμειακή — η διαγραφή θα στείλει εντολή ακύρωσης (VOID_LINE) όταν πατήσετε «Καταχώρηση».</div>
            )}
            {batchActionDlg?.pendingDeleted && (
              <div className="mt-1 text-rose-700 font-bold">Σε εκκρεμότητα διαγραφής — πατήστε «Καταχώρηση» για να ολοκληρωθεί ή «Άκυρο Διαγραφής» για ανάκληση.</div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 mt-2">
            {!batchActionDlg?.pendingDeleted && (
              <Button
                onClick={() => reorderSubmittedItem(batchActionDlg?.item)}
                className="bg-blue-600 hover:bg-blue-700 text-white h-11"
                data-testid="mr-batch-reorder"
              >Επαναπαραγγελία (Ίδιο είδος)</Button>
            )}
            <Button
              variant="outline"
              className={batchActionDlg?.pendingDeleted
                ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-11"
                : "border-red-300 text-red-700 hover:bg-red-50 h-11"}
              onClick={() => toggleDeleteSubmitted(batchActionDlg.batchNo, batchActionDlg.itemIdx)}
              data-testid="mr-batch-delete"
            >{batchActionDlg?.pendingDeleted ? "Άκυρο Διαγραφής" : "Σήμανση Διαγραφής"}</Button>
            <Button variant="ghost" onClick={() => setBatchActionDlg(null)} data-testid="mr-batch-cancel">Κλείσιμο</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* «Καταχώρηση Παραγγελίας» — 3 επιλογές (παράλληλο με desktop) */}
      <Dialog open={submitChoiceOpen} onOpenChange={setSubmitChoiceOpen}>
        <DialogContent className="max-w-md p-0 gap-0" data-testid="mr-submit-choice-dialog">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-lg font-bold">
              Καταχώρηση Παραγγελίας — {openTable?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="p-3 space-y-2">
            <button
              onClick={handleFullSendToRegister}
              disabled={busy}
              className="w-full text-left rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 text-white p-3 flex items-start gap-3"
              data-testid="mr-submit-choice-full"
            >
              <Send size={22} className="shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-bold">Αποστολή στην Ταμειακή</div>
                <div className="text-xs opacity-90 mt-0.5">Καταχώρηση + όλα τα ασταλέντα μεταφέρονται αυτόματα στην ταμειακή.</div>
              </div>
            </button>
            <button
              onClick={handlePartialStart}
              disabled={busy}
              className="w-full text-left rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 text-white p-3 flex items-start gap-3"
              data-testid="mr-submit-choice-partial"
            >
              <ListChecks size={22} className="shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-bold">Μερική Αποστολή</div>
                <div className="text-xs opacity-90 mt-0.5">Επιλογή συγκεκριμένων ειδών για αποστολή στην ταμειακή. Αν δεν επιλεχθεί τίποτα, τα είδη απλά αποθηκεύονται στο τραπέζι.</div>
              </div>
            </button>
          </div>
          <DialogFooter className="px-3 pb-3 pt-1 border-t bg-slate-50">
            <Button
              variant="outline"
              onClick={() => setSubmitChoiceOpen(false)}
              data-testid="mr-submit-choice-cancel"
              className="w-full h-11"
            >Ακύρωση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Μερική αποστολή — επιλογή τμχ ανά είδος */}
      <Dialog open={partialOpen} onOpenChange={(v) => { if (!v) { setPartialOpen(false); setPartialQty(new Map()); } }}>
        <DialogContent className="max-w-md p-0 gap-0 max-h-[85vh] flex flex-col" data-testid="mr-partial-dialog">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="text-base">
              Μερική Αποστολή — {openTable?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-3 py-2 divide-y divide-slate-100">
            {openTable && allUnsentSelections(openTable).length === 0 && (
              <div className="text-center text-slate-400 italic py-6">Δεν υπάρχουν είδη προς αποστολή</div>
            )}
            {openTable && (openTable.submitted_batches || []).flatMap((b) =>
              (b.items || []).map((it, i) => {
                if (it.corrected_deleted || it.paid_partial || it.sent_to_register) return null;
                const key = `${b.batch_no}:${i}`;
                const maxQty = Number(it.qty || 0);
                const cur = Number(partialQty.get(key) || 0);
                return (
                  <div key={key} className="py-2 flex items-center gap-2" data-testid={`mr-partial-row-${b.batch_no}-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{it.description}</div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        διαθέσιμα {maxQty} · {Number(it.price || 0).toFixed(2)} €
                      </div>
                    </div>
                    <button
                      onClick={() => setPartialQty((m) => { const n = new Map(m); const c = Number(n.get(key) || 0); if (c <= 1) n.delete(key); else n.set(key, c - 1); return n; })}
                      className="w-8 h-8 rounded bg-slate-200 active:bg-slate-300"
                      data-testid={`mr-partial-minus-${b.batch_no}-${i}`}
                    ><Minus size={14} className="mx-auto" /></button>
                    <span className="w-8 text-center font-bold font-mono" data-testid={`mr-partial-qty-${b.batch_no}-${i}`}>{cur}</span>
                    <button
                      onClick={() => setPartialQty((m) => { const n = new Map(m); const c = Number(n.get(key) || 0); if (c < maxQty) n.set(key, c + 1); return n; })}
                      disabled={cur >= maxQty}
                      className="w-8 h-8 rounded bg-slate-200 active:bg-slate-300 disabled:opacity-40"
                      data-testid={`mr-partial-plus-${b.batch_no}-${i}`}
                    ><Plus size={14} className="mx-auto" /></button>
                  </div>
                );
              }),
            )}
          </div>
          <DialogFooter className="px-3 py-3 border-t shrink-0 grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={() => { setPartialOpen(false); setPartialQty(new Map()); }}
              data-testid="mr-partial-cancel"
            >Ακύρωση</Button>
            <Button
              onClick={handlePartialConfirm}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="mr-partial-confirm"
            >
              <Send size={14} className="mr-1" />
              {(() => {
                const totalQty = Array.from(partialQty.values()).reduce(
                  (s, q) => s + Number(q || 0), 0,
                );
                return totalQty === 0
                  ? "Αποθήκευση"
                  : `Αποστολή (${totalQty} τμχ)`;
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flush-warning before fiscal close (mirror desktop pendingFlushOpen) */}
      <Dialog open={flushWarnOpen} onOpenChange={setFlushWarnOpen}>
        <DialogContent className="max-w-md p-0 gap-0" data-testid="mr-flush-warn-dialog">
          <DialogHeader className="px-4 py-3 border-b bg-amber-50">
            <DialogTitle className="text-base flex items-center gap-2 text-amber-800">
              <Receipt size={18} /> Είδη σε εκκρεμότητα
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 text-sm text-slate-700 leading-relaxed">
            Το τραπέζι «{openTable?.name}» έχει είδη που δεν έχουν σταλεί ακόμη στην ταμειακή.
            Επιλέξτε πώς θέλετε να προχωρήσετε:
          </div>
          {openTable && (() => {
            const unsentCount = allUnsentSelections(openTable).length + cart.length;
            return unsentCount > 0 ? (
              <div className="px-4 pb-3 text-xs font-mono text-amber-800">
                {unsentCount} είδ{unsentCount === 1 ? "ος" : "η"} προς αποστολή · {fmtEUR(totalsTotal)}
              </div>
            ) : null;
          })()}
          <div className="px-3 pb-3 space-y-2">
            <Button
              onClick={handleFlushAndClose}
              disabled={busy}
              className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 text-white font-bold leading-tight flex flex-col items-center justify-center gap-0.5"
              data-testid="mr-flush-warn-confirm"
            >
              <span className="text-base flex items-center gap-1"><Send size={14} /> Αποστολή στην Ταμειακή</span>
              <span className="text-[10px] font-normal opacity-90">Όλα τα είδη γίνονται φορολογικά → δέχονται μετρητά & κάρτα.</span>
            </Button>
            <Button
              onClick={continueCollectionCashOnly}
              disabled={busy}
              variant="outline"
              className="w-full h-16 border-2 border-amber-400 text-amber-800 font-bold leading-tight flex flex-col items-center justify-center gap-0.5"
              data-testid="mr-flush-warn-continue-collection"
            >
              <span className="text-base flex items-center gap-1"><HandCoins size={14} /> Συνέχιση Είσπραξης</span>
              <span className="text-[10px] font-normal text-slate-600">Τα ασταλέντα είδη εισπράττονται μόνο μετρητά.</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => setFlushWarnOpen(false)}
              data-testid="mr-flush-warn-cancel"
              className="w-full h-10 text-slate-600"
            >Άκυρο</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Πληρωμή — full-screen mobile dialog with red back button.
          Replaces the centered modal so all payment controls have room
          to breathe on small phones. */}
      {closeDlgOpen && openTable && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="mr-payment-dialog" role="dialog" aria-modal="true">
          <div className="h-14 shrink-0 bg-emerald-50 border-b border-emerald-200 flex items-center px-3 gap-2">
            <Button
              size="sm"
              onClick={() => { setCloseDlgOpen(false); setClosePayments([]); setCloseComment(""); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="mr-payment-back"
            ><ChevronLeft size={20} /> Πίσω</Button>
            <div className="flex-1 font-bold text-base truncate">
              Πληρωμή — {openTable.name}
            </div>
            <div className="text-xs text-slate-600 font-mono shrink-0">
              <span className="font-bold">{fmtEUR(totalsTotal)}</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <MobilePaymentDialog
              total={totalsTotal}
              payments={payments}
              split={closePayments}
              setSplit={setClosePayments}
              busy={busy}
              cartEmpty={totalsTotal === 0}
              onPrintOrder={paymentDialogPrintProforma}
              onSubmit={closeTableNow}
              onPartialOpen={openPaymentPartial}
            />
          </div>
        </div>
      )}

      {/* Μερική Είσπραξη — full-screen multi-stage flow (pick → warn → pay).
          Mirrors desktop Restaurant.jsx (handlePartialPick + confirm + submit). */}
      {paymentPartialOpen && openTable && (
        <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="mr-payment-partial-dialog" role="dialog" aria-modal="true">
          {/* Top bar — red back button + dynamic title per stage */}
          <div className="h-14 shrink-0 bg-amber-50 border-b border-amber-200 flex items-center px-3 gap-2">
            <Button
              size="sm"
              onClick={() => {
                if (paymentPartialStage === "pick") {
                  setPaymentPartialOpen(false);
                  setPaymentPartialQty(new Map());
                } else if (paymentPartialStage === "warn") {
                  setPaymentPartialStage("pick");
                } else {
                  // pay → warn (if needed) else pick
                  let hasUnsent = false;
                  for (const key of paymentPartialQty.keys()) {
                    const [bn, ii] = key.split(":").map(Number);
                    const b = (openTable.submitted_batches || []).find((x) => x.batch_no === bn);
                    const it = b?.items?.[ii];
                    if (it && !it.sent_to_register) { hasUnsent = true; break; }
                  }
                  setPaymentPartialStage(hasUnsent ? "warn" : "pick");
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="mr-pay-partial-back"
            ><ChevronLeft size={20} /> Πίσω</Button>
            <div className="flex-1 font-bold text-base truncate flex items-center gap-2 text-amber-900">
              <Receipt size={18} />
              {paymentPartialStage === "pick" && `Μερική Είσπραξη — ${openTable.name}`}
              {paymentPartialStage === "warn" && "Είδη εκτός ταμειακής"}
              {paymentPartialStage === "pay" && `Πληρωμή — ${fmtEUR(paymentPartialTotal)}`}
            </div>
          </div>

          {/* Stage 1 — pick */}
          {paymentPartialStage === "pick" && (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-2 divide-y divide-slate-100">
                <div className="text-xs text-slate-600 italic pb-2">
                  Επιλέξτε είδη για άμεση είσπραξη. Τα υπόλοιπα θα παραμείνουν ανοιχτά στο τραπέζι.
                </div>
                {(openTable.submitted_batches || []).flatMap((b) =>
                  (b.items || []).map((it, i) => {
                    if (it.corrected_deleted || it.paid_partial) return null;
                    const key = `${b.batch_no}:${i}`;
                    const maxQty = Number(it.qty || 0);
                    const cur = Number(paymentPartialQty.get(key) || 0);
                    const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
                    return (
                      <div key={key} className="py-2 flex items-center gap-2" data-testid={`mr-pay-partial-row-${b.batch_no}-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{it.description}</div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            {maxQty} διαθ. · {unit.toFixed(2)} €
                            {it.sent_to_register && <span className="ml-1 text-emerald-700 font-bold">· φορ.</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => setPaymentPartialQty((m) => { const n = new Map(m); const c = Number(n.get(key) || 0); if (c <= 1) n.delete(key); else n.set(key, c - 1); return n; })}
                          className="w-9 h-9 rounded bg-slate-200 active:bg-slate-300"
                          data-testid={`mr-pay-partial-minus-${b.batch_no}-${i}`}
                        ><Minus size={14} className="mx-auto" /></button>
                        <span className="w-8 text-center font-bold font-mono">{cur}</span>
                        <button
                          onClick={() => setPaymentPartialQty((m) => { const n = new Map(m); const c = Number(n.get(key) || 0); if (c < maxQty) n.set(key, c + 1); return n; })}
                          disabled={cur >= maxQty}
                          className="w-9 h-9 rounded bg-slate-200 active:bg-slate-300 disabled:opacity-40"
                          data-testid={`mr-pay-partial-plus-${b.batch_no}-${i}`}
                        ><Plus size={14} className="mx-auto" /></button>
                      </div>
                    );
                  }),
                )}
                {(openTable.submitted_batches || []).every((b) => (b.items || []).every((it) => it.corrected_deleted || it.paid_partial)) && (
                  <div className="text-center text-slate-400 italic py-6">Δεν υπάρχουν είδη προς είσπραξη</div>
                )}
              </div>
              <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 flex justify-between items-baseline shrink-0">
                <span className="text-xs font-bold uppercase text-amber-900">Σύνολο επιλογής</span>
                <span className="font-mono tabular text-lg font-extrabold text-amber-900">{fmtEUR(paymentPartialTotal)}</span>
              </div>
              <div className="shrink-0 bg-white border-t-2 border-slate-300 p-2 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setPaymentPartialOpen(false); setPaymentPartialQty(new Map()); }}
                  className="h-12"
                  data-testid="mr-pay-partial-cancel"
                >Ακύρωση</Button>
                <Button
                  onClick={confirmPartialPick}
                  disabled={busy || Array.from(paymentPartialQty.values()).every((q) => !q)}
                  className="h-12 bg-amber-600 hover:bg-amber-700 text-white font-bold"
                  data-testid="mr-pay-partial-confirm"
                >Συνέχεια <ChevronLeft size={16} className="ml-1 rotate-180" /></Button>
              </div>
            </>
          )}

          {/* Stage 2 — warn (one or more selected items are unsent) */}
          {paymentPartialStage === "warn" && (
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
              <div className="rounded-lg bg-amber-50 border-2 border-amber-300 p-3 text-sm text-amber-900 leading-snug">
                <div className="font-bold mb-1">⚠ Είδη εκτός ταμειακής</div>
                Επιλέξατε είδη που δεν έχουν σταλεί ακόμα στην ταμειακή. Πώς θέλετε να προχωρήσετε;
              </div>
              <Button
                onClick={() => { setPaymentPartialCashOnly(false); setPaymentPartialStage("pay"); }}
                className="h-16 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-left p-4 flex flex-col items-start gap-0.5"
                data-testid="mr-pay-partial-warn-ecr"
              >
                <span className="text-base">Αποστολή στην Ταμειακή & Πληρωμή</span>
                <span className="text-xs font-normal opacity-90">Στέλνονται φορολογικά και εισπράττονται.</span>
              </Button>
              <Button
                onClick={() => { setPaymentPartialCashOnly(true); setPaymentPartialStage("pay"); }}
                variant="outline"
                className="h-16 border-2 border-slate-300 font-bold text-left p-4 flex flex-col items-start gap-0.5"
                data-testid="mr-pay-partial-warn-cash"
              >
                <span className="text-base">Μόνο Είσπραξη (χωρίς ταμειακή)</span>
                <span className="text-xs font-normal text-slate-500">Καταγραφή πληρωμής χωρίς φορολογική αποστολή.</span>
              </Button>
            </div>
          )}

          {/* Stage 3 — pay */}
          {paymentPartialStage === "pay" && (
            <div className="flex-1 overflow-y-auto p-3">
              <MobilePaymentDialog
                total={paymentPartialTotal}
                payments={payments}
                split={paymentPartialSplit}
                setSplit={setPaymentPartialSplit}
                busy={busy}
                cartEmpty={paymentPartialTotal === 0}
                onPrintOrder={paymentDialogPrintProforma}
                onSubmit={submitPaymentPartial}
                onPartialOpen={null}
                hidePartialButton
                cashOnly={paymentPartialCashOnly}
                breakdown={paymentBreakdown}
              />
            </div>
          )}
        </div>
      )}

      {/* Quick Note (Σχόλια) — picks a saved note or types ad-hoc + prints
          to selected printers. Does NOT mutate the table state. */}
      <QuickNoteDialog
        open={quickNoteOpen}
        onOpenChange={setQuickNoteOpen}
        table={openTable}
      />
    </div>
  );
}
